// src/utils.ts
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
function cn(...inputs) {
  return twMerge(clsx(inputs));
}
function getInitials(name) {
  if (!name) {
    return "";
  }
  return name.trim().split(/\s+/).map((part) => part[0]).join("").toUpperCase().slice(0, 2);
}

// src/components/ui/accordion.tsx
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { ChevronDownIcon } from "lucide-react";
import { jsx, jsxs } from "react/jsx-runtime";
function Accordion({
  ...props
}) {
  return /* @__PURE__ */ jsx(AccordionPrimitive.Root, { "data-slot": "accordion", ...props });
}
function AccordionItem({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx(
    AccordionPrimitive.Item,
    {
      "data-slot": "accordion-item",
      className: cn("border-b last:border-b-0", className),
      ...props
    }
  );
}
function AccordionTrigger({
  className,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsx(AccordionPrimitive.Header, { className: "flex", children: /* @__PURE__ */ jsxs(
    AccordionPrimitive.Trigger,
    {
      "data-slot": "accordion-trigger",
      className: cn(
        "focus-visible:border-ring focus-visible:ring-ring/50 flex flex-1 items-start justify-between gap-4 rounded-md py-4 text-left text-sm font-medium transition-all outline-none hover:underline focus-visible:ring-[3px] disabled:pointer-events-none disabled:opacity-50 [&[data-state=open]>svg]:rotate-180",
        className
      ),
      ...props,
      children: [
        children,
        /* @__PURE__ */ jsx(
          ChevronDownIcon,
          {
            className: "text-muted-foreground pointer-events-none size-4 shrink-0 translate-y-0.5 transition-transform duration-200"
          }
        )
      ]
    }
  ) });
}
function AccordionContent({
  className,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsx(
    AccordionPrimitive.Content,
    {
      "data-slot": "accordion-content",
      className: "data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down overflow-hidden text-sm",
      ...props,
      children: /* @__PURE__ */ jsx("div", { className: cn("pt-0 pb-4", className), children })
    }
  );
}

// src/components/ui/admin-sidebar.tsx
import { jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
function AdminSidebar({
  navItems,
  user,
  logo,
  appName = "MorningAI",
  appSubtitle = "Owner Console",
  className,
  renderLink,
  ariaLabel = "Primary navigation"
}) {
  const defaultRenderLink = ({
    href,
    className: linkClassName,
    children
  }) => /* @__PURE__ */ jsx2("a", { href, className: linkClassName, children });
  const LinkComponent = renderLink || defaultRenderLink;
  return /* @__PURE__ */ jsxs2(
    "aside",
    {
      "aria-label": ariaLabel,
      className: cn(
        "w-64 border-r border-neutral-200 bg-white shadow-sm flex flex-col dark:border-neutral-700 dark:bg-neutral-900",
        className
      ),
      children: [
        /* @__PURE__ */ jsx2("div", { className: "px-6 py-5 border-b border-neutral-200 dark:border-neutral-700", children: /* @__PURE__ */ jsxs2("div", { className: "flex items-center gap-3", children: [
          logo || /* @__PURE__ */ jsx2("div", { className: "h-9 w-9 rounded-xl bg-primary-500 text-white flex items-center justify-center font-semibold", children: "M" }),
          /* @__PURE__ */ jsxs2("div", { children: [
            /* @__PURE__ */ jsx2("div", { className: "text-sm font-semibold text-neutral-900 dark:text-neutral-100", children: appName }),
            /* @__PURE__ */ jsx2("div", { className: "text-xs text-neutral-500 dark:text-neutral-400", children: appSubtitle })
          ] })
        ] }) }),
        /* @__PURE__ */ jsx2("nav", { className: "px-3 py-4 space-y-1 flex-1", children: navItems.map((item) => /* @__PURE__ */ jsxs2(
          LinkComponent,
          {
            href: item.href,
            className: cn(
              "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors",
              item.active ? "bg-primary-50 text-primary-700 font-medium dark:bg-primary-900/20 dark:text-primary-400" : "text-neutral-500 hover:bg-neutral-50 dark:text-neutral-400 dark:hover:bg-neutral-800"
            ),
            children: [
              item.icon && /* @__PURE__ */ jsx2(item.icon, { className: "h-5 w-5" }),
              item.label
            ]
          },
          item.href
        )) }),
        /* @__PURE__ */ jsx2("div", { className: "px-4 py-4 border-t border-neutral-200 dark:border-neutral-700", children: /* @__PURE__ */ jsxs2("div", { className: "flex items-center gap-3", children: [
          user.avatar ? /* @__PURE__ */ jsx2(
            "img",
            {
              src: user.avatar,
              alt: "",
              "aria-hidden": "true",
              className: "h-8 w-8 rounded-full object-cover"
            }
          ) : /* @__PURE__ */ jsx2(
            "div",
            {
              "aria-hidden": "true",
              className: "h-8 w-8 rounded-full bg-neutral-100 dark:bg-neutral-700 flex items-center justify-center text-xs font-medium text-neutral-600 dark:text-neutral-300",
              children: user.name.charAt(0).toUpperCase()
            }
          ),
          /* @__PURE__ */ jsxs2("div", { className: "flex-1 min-w-0", children: [
            /* @__PURE__ */ jsx2("div", { className: "text-sm font-medium text-neutral-900 dark:text-neutral-100 truncate", children: user.name }),
            /* @__PURE__ */ jsx2("div", { className: "text-xs text-neutral-500 dark:text-neutral-400 truncate", children: user.role })
          ] })
        ] }) })
      ]
    }
  );
}
AdminSidebar.displayName = "AdminSidebar";

// src/components/ui/admin-topbar.tsx
import * as React from "react";
import { jsx as jsx3, jsxs as jsxs3 } from "react/jsx-runtime";
function AdminTopbar({
  user,
  title = "Platform Overview",
  searchPlaceholder = "Search tenants, events, agents...",
  onSearch,
  className,
  children
}) {
  const [searchQuery, setSearchQuery] = React.useState("");
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    onSearch?.(value);
  };
  return /* @__PURE__ */ jsxs3(
    "header",
    {
      className: cn(
        "h-14 flex items-center justify-between px-6 border-b border-neutral-200 bg-white dark:border-neutral-700 dark:bg-neutral-900",
        className
      ),
      children: [
        /* @__PURE__ */ jsx3("div", { className: "text-sm text-neutral-500 dark:text-neutral-400", children: title }),
        /* @__PURE__ */ jsxs3("div", { className: "flex items-center gap-4", children: [
          /* @__PURE__ */ jsx3(
            "input",
            {
              type: "text",
              placeholder: searchPlaceholder,
              "aria-label": searchPlaceholder,
              value: searchQuery,
              onChange: handleSearchChange,
              className: "h-9 w-64 px-3 rounded-full border border-neutral-200 bg-neutral-50 text-xs focus:outline-none focus:ring-2 focus:ring-primary-100 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-100"
            }
          ),
          children,
          user.avatar ? /* @__PURE__ */ jsx3(
            "img",
            {
              src: user.avatar,
              alt: user.name,
              className: "h-8 w-8 rounded-full object-cover"
            }
          ) : /* @__PURE__ */ jsx3(
            "div",
            {
              "aria-label": user.name,
              role: "img",
              className: "h-8 w-8 rounded-full bg-neutral-100 dark:bg-neutral-700 flex items-center justify-center text-xs font-medium text-neutral-600 dark:text-neutral-300",
              children: user.name.charAt(0).toUpperCase()
            }
          )
        ] })
      ]
    }
  );
}
AdminTopbar.displayName = "AdminTopbar";

// src/components/ui/admin-shell.tsx
import { jsx as jsx4, jsxs as jsxs4 } from "react/jsx-runtime";
function AdminShell({
  navItems,
  user,
  logo,
  appName,
  appSubtitle,
  topbarTitle,
  searchPlaceholder,
  onSearch,
  className,
  sidebarClassName,
  sidebarAriaLabel,
  topbarClassName,
  mainClassName,
  children,
  renderLink,
  topbarChildren,
  rightPanel,
  rightPanelClassName,
  rightPanelAriaLabel
}) {
  return /* @__PURE__ */ jsx4(
    "div",
    {
      className: cn(
        "min-h-screen bg-neutral-50 text-neutral-900 dark:bg-neutral-950 dark:text-neutral-100",
        className
      ),
      children: /* @__PURE__ */ jsxs4("div", { className: "flex h-screen", children: [
        /* @__PURE__ */ jsx4(
          AdminSidebar,
          {
            navItems,
            user,
            logo,
            appName,
            appSubtitle,
            className: sidebarClassName,
            renderLink,
            ariaLabel: sidebarAriaLabel
          }
        ),
        /* @__PURE__ */ jsxs4("div", { className: "flex-1 flex flex-col overflow-hidden", children: [
          /* @__PURE__ */ jsx4(
            AdminTopbar,
            {
              user,
              title: topbarTitle,
              searchPlaceholder,
              onSearch,
              className: topbarClassName,
              children: topbarChildren
            }
          ),
          /* @__PURE__ */ jsxs4("div", { className: "flex-1 flex overflow-hidden", children: [
            /* @__PURE__ */ jsx4("main", { className: cn("flex-1 overflow-auto p-6", mainClassName), children }),
            rightPanel && /* @__PURE__ */ jsx4(
              "aside",
              {
                "aria-label": rightPanelAriaLabel,
                className: cn(
                  "hidden lg:block w-80 border-l border-neutral-200 bg-white overflow-auto dark:border-neutral-700 dark:bg-neutral-900",
                  rightPanelClassName
                ),
                children: rightPanel
              }
            )
          ] })
        ] })
      ] })
    }
  );
}
AdminShell.displayName = "AdminShell";

// src/components/ui/alert-dialog.tsx
import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog";

// src/components/ui/button.tsx
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import { jsx as jsx5 } from "react/jsx-runtime";
var buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive btn-press font-['Public_Sans',sans-serif]",
  {
    variants: {
      variant: {
        default: "bg-primary-500 text-white shadow-sm hover:bg-primary-600 active:bg-primary-700",
        destructive: "bg-error-500 text-white shadow-sm hover:bg-error-600 active:bg-error-700 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40",
        outline: "border border-neutral-300 bg-white text-neutral-700 shadow-sm hover:bg-neutral-50 hover:border-primary-500 hover:text-primary-500 dark:bg-neutral-800 dark:border-neutral-600 dark:text-neutral-200 dark:hover:bg-neutral-700",
        secondary: "bg-neutral-100 text-neutral-700 shadow-sm hover:bg-neutral-200 dark:bg-neutral-700 dark:text-neutral-200 dark:hover:bg-neutral-600",
        ghost: "text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900 dark:text-neutral-300 dark:hover:bg-neutral-800 dark:hover:text-neutral-100",
        link: "text-primary-500 underline-offset-4 hover:underline hover:text-primary-600",
        success: "bg-success-500 text-white shadow-sm hover:bg-success-600 active:bg-success-700",
        warning: "bg-warning-500 text-neutral-900 shadow-sm hover:bg-warning-600 active:bg-warning-700"
      },
      size: {
        default: "h-10 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-lg gap-1.5 px-3 text-xs has-[>svg]:px-2.5",
        lg: "h-12 rounded-lg px-6 text-base has-[>svg]:px-4",
        icon: "size-10 rounded-lg"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default"
    }
  }
);
function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}) {
  const Comp = asChild ? Slot : "button";
  return /* @__PURE__ */ jsx5(
    Comp,
    {
      "data-slot": "button",
      className: cn(buttonVariants({ variant, size, className })),
      ...props
    }
  );
}

// src/components/ui/alert-dialog.tsx
import { jsx as jsx6, jsxs as jsxs5 } from "react/jsx-runtime";
function AlertDialog({
  ...props
}) {
  return /* @__PURE__ */ jsx6(AlertDialogPrimitive.Root, { "data-slot": "alert-dialog", ...props });
}
function AlertDialogTrigger({
  ...props
}) {
  return /* @__PURE__ */ jsx6(AlertDialogPrimitive.Trigger, { "data-slot": "alert-dialog-trigger", ...props });
}
function AlertDialogPortal({
  ...props
}) {
  return /* @__PURE__ */ jsx6(AlertDialogPrimitive.Portal, { "data-slot": "alert-dialog-portal", ...props });
}
function AlertDialogOverlay({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx6(
    AlertDialogPrimitive.Overlay,
    {
      "data-slot": "alert-dialog-overlay",
      className: cn(
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50",
        className
      ),
      ...props
    }
  );
}
function AlertDialogContent({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsxs5(AlertDialogPortal, { children: [
    /* @__PURE__ */ jsx6(AlertDialogOverlay, {}),
    /* @__PURE__ */ jsx6(
      AlertDialogPrimitive.Content,
      {
        "data-slot": "alert-dialog-content",
        className: cn(
          "bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[50%] z-50 grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border p-6 shadow-lg duration-200 sm:max-w-lg",
          className
        ),
        ...props
      }
    )
  ] });
}
function AlertDialogHeader({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx6(
    "div",
    {
      "data-slot": "alert-dialog-header",
      className: cn("flex flex-col gap-2 text-center sm:text-left", className),
      ...props
    }
  );
}
function AlertDialogFooter({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx6(
    "div",
    {
      "data-slot": "alert-dialog-footer",
      className: cn("flex flex-col-reverse gap-2 sm:flex-row sm:justify-end", className),
      ...props
    }
  );
}
function AlertDialogTitle({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx6(
    AlertDialogPrimitive.Title,
    {
      "data-slot": "alert-dialog-title",
      className: cn("text-lg font-semibold", className),
      ...props
    }
  );
}
function AlertDialogDescription({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx6(
    AlertDialogPrimitive.Description,
    {
      "data-slot": "alert-dialog-description",
      className: cn("text-muted-foreground text-sm", className),
      ...props
    }
  );
}
function AlertDialogAction({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx6(AlertDialogPrimitive.Action, { className: cn(buttonVariants(), className), ...props });
}
function AlertDialogCancel({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx6(
    AlertDialogPrimitive.Cancel,
    {
      className: cn(buttonVariants({ variant: "outline" }), className),
      ...props
    }
  );
}

// src/components/ui/alert.tsx
import { cva as cva2 } from "class-variance-authority";
import { jsx as jsx7 } from "react/jsx-runtime";
var alertVariants = cva2(
  "relative w-full rounded-lg border px-4 py-3 text-sm grid has-[>svg]:grid-cols-[calc(var(--spacing)*4)_1fr] grid-cols-[0_1fr] has-[>svg]:gap-x-3 gap-y-0.5 items-start [&>svg]:size-4 [&>svg]:translate-y-0.5 [&>svg]:text-current",
  {
    variants: {
      variant: {
        default: "bg-card text-card-foreground",
        destructive: "text-destructive bg-card [&>svg]:text-current *:data-[slot=alert-description]:text-destructive/90"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);
function Alert({
  className,
  variant,
  ...props
}) {
  return /* @__PURE__ */ jsx7(
    "div",
    {
      "data-slot": "alert",
      role: "alert",
      className: cn(alertVariants({ variant }), className),
      ...props
    }
  );
}
function AlertTitle({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx7(
    "div",
    {
      "data-slot": "alert-title",
      className: cn("col-start-2 line-clamp-1 min-h-4 font-medium tracking-tight", className),
      ...props
    }
  );
}
function AlertDescription({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx7(
    "div",
    {
      "data-slot": "alert-description",
      className: cn(
        "text-muted-foreground col-start-2 grid justify-items-start gap-1 text-sm [&_p]:leading-relaxed",
        className
      ),
      ...props
    }
  );
}

// src/components/ui/apple-button.tsx
import * as React2 from "react";
import { Slot as Slot2 } from "@radix-ui/react-slot";
import { cva as cva3 } from "class-variance-authority";
import { motion } from "framer-motion";
import { jsx as jsx8 } from "react/jsx-runtime";
var appleButtonVariants = cva3(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:ring-[3px] focus-visible:ring-offset-0",
  {
    variants: {
      variant: {
        primary: "bg-primary-600 dark:bg-primary-500 text-white shadow-sm hover:bg-primary-700 dark:hover:bg-primary-600 hover:shadow-md focus-visible:ring-primary-600/30 active:shadow-sm",
        secondary: "bg-neutral-100 dark:bg-neutral-700 text-neutral-900 dark:text-white shadow-sm hover:bg-neutral-200 dark:hover:bg-neutral-600 hover:shadow-md focus-visible:ring-neutral-500/30 active:shadow-sm",
        destructive: "bg-error-600 dark:bg-error-500 text-white shadow-sm hover:bg-error-700 dark:hover:bg-error-600 hover:shadow-md focus-visible:ring-error-600/30 active:shadow-sm",
        outline: "border border-neutral-300 dark:border-neutral-600 bg-white/80 dark:bg-neutral-800/80 backdrop-blur-sm shadow-xs hover:bg-neutral-50 dark:hover:bg-neutral-700 hover:text-neutral-900 dark:hover:text-white focus-visible:ring-primary-600/20 active:bg-neutral-100/80 dark:active:bg-neutral-700/80",
        ghost: "hover:bg-neutral-100/80 dark:hover:bg-neutral-700/80 hover:text-neutral-900 dark:hover:text-white focus-visible:ring-primary-600/20 active:bg-neutral-100 dark:active:bg-neutral-700",
        link: "text-primary-600 dark:text-primary-400 underline-offset-4 hover:underline focus-visible:ring-primary-600/20",
        filled: "bg-neutral-100 dark:bg-neutral-700 text-neutral-900 dark:text-white shadow-xs hover:shadow-sm focus-visible:ring-neutral-500/30 active:shadow-xs",
        tinted: "bg-primary-600/10 dark:bg-primary-400/20 text-primary-600 dark:text-primary-400 hover:bg-primary-600/20 dark:hover:bg-primary-400/30 focus-visible:ring-primary-600/30 active:bg-primary-600/15 dark:active:bg-primary-400/25"
      },
      size: {
        sm: "h-8 rounded-lg gap-1.5 px-3 text-sm has-[>svg]:px-2.5",
        default: "h-10 rounded-xl gap-2 px-4 text-sm has-[>svg]:px-3",
        lg: "h-12 rounded-xl gap-2 px-6 text-base has-[>svg]:px-4",
        icon: "size-10 rounded-xl",
        "icon-sm": "size-8 rounded-lg",
        "icon-lg": "size-12 rounded-xl"
      },
      haptic: {
        none: "",
        light: "",
        medium: "",
        heavy: ""
      }
    },
    defaultVariants: {
      variant: "primary",
      size: "default",
      haptic: "medium"
    }
  }
);
function AppleButton({
  className,
  variant,
  size,
  haptic = "medium",
  asChild = false,
  disabled = false,
  children,
  onClick,
  onHapticFeedback,
  springConfig,
  ...props
}) {
  const buttonRef = React2.useRef(null);
  const Comp = asChild ? Slot2 : motion.button;
  const handleClick = React2.useCallback((e) => {
    if (disabled) return;
    if (onHapticFeedback && haptic !== "none" && haptic != null && buttonRef.current) {
      onHapticFeedback(buttonRef.current, haptic);
    }
    onClick?.(e);
  }, [disabled, haptic, onClick, onHapticFeedback]);
  const motionProps = asChild ? {} : {
    whileHover: disabled ? {} : { scale: 1.02 },
    whileTap: disabled ? {} : { scale: 0.98 },
    transition: springConfig
  };
  return /* @__PURE__ */ jsx8(
    Comp,
    {
      ref: buttonRef,
      "data-slot": "button",
      className: cn(appleButtonVariants({ variant, size, className })),
      disabled,
      onClick: handleClick,
      ...motionProps,
      ...props,
      children
    }
  );
}
AppleButton.displayName = "AppleButton";

// src/components/ui/aspect-ratio.tsx
import * as AspectRatioPrimitive from "@radix-ui/react-aspect-ratio";
import { jsx as jsx9 } from "react/jsx-runtime";
function AspectRatio({
  ...props
}) {
  return /* @__PURE__ */ jsx9(AspectRatioPrimitive.Root, { "data-slot": "aspect-ratio", ...props });
}

// src/components/ui/avatar.tsx
import * as AvatarPrimitive from "@radix-ui/react-avatar";
import { jsx as jsx10 } from "react/jsx-runtime";
function Avatar({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx10(
    AvatarPrimitive.Root,
    {
      "data-slot": "avatar",
      className: cn("relative flex size-10 shrink-0 overflow-hidden rounded-full", className),
      ...props
    }
  );
}
function AvatarImage({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx10(
    AvatarPrimitive.Image,
    {
      "data-slot": "avatar-image",
      className: cn("aspect-square size-full object-cover", className),
      ...props
    }
  );
}
function AvatarFallback({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx10(
    AvatarPrimitive.Fallback,
    {
      "data-slot": "avatar-fallback",
      className: cn(
        "bg-muted flex size-full items-center justify-center rounded-full",
        className
      ),
      ...props
    }
  );
}

// src/components/ui/badge.tsx
import { Slot as Slot3 } from "@radix-ui/react-slot";
import { cva as cva4 } from "class-variance-authority";
import { jsx as jsx11 } from "react/jsx-runtime";
var badgeVariants = cva4(
  "inline-flex items-center justify-center rounded-lg border px-2.5 py-1 text-xs font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden font-['Public_Sans',sans-serif]",
  {
    variants: {
      variant: {
        default: "border-transparent bg-primary-500 text-white [a&]:hover:bg-primary-600",
        secondary: "border-transparent bg-neutral-100 text-neutral-700 [a&]:hover:bg-neutral-200 dark:bg-neutral-700 dark:text-neutral-200",
        destructive: "border-transparent bg-error-500 text-white [a&]:hover:bg-error-600 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40",
        outline: "border-neutral-300 text-neutral-700 bg-white [a&]:hover:bg-neutral-50 dark:border-neutral-600 dark:text-neutral-200 dark:bg-neutral-800",
        success: "border-transparent bg-success-500 text-white [a&]:hover:bg-success-600",
        warning: "border-transparent bg-warning-500 text-neutral-900 [a&]:hover:bg-warning-600",
        pink: "border-transparent bg-pink-500 text-white [a&]:hover:bg-pink-600",
        cyan: "border-transparent bg-cyan-500 text-white [a&]:hover:bg-cyan-600"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);
function Badge({
  className,
  variant,
  asChild = false,
  ...props
}) {
  const Comp = asChild ? Slot3 : "span";
  return /* @__PURE__ */ jsx11(
    Comp,
    {
      "data-slot": "badge",
      className: cn(badgeVariants({ variant }), className),
      ...props
    }
  );
}

// src/components/ui/breadcrumb.tsx
import { Slot as Slot4 } from "@radix-ui/react-slot";
import { ChevronRight, MoreHorizontal } from "lucide-react";
import { jsx as jsx12, jsxs as jsxs6 } from "react/jsx-runtime";
function Breadcrumb({
  ...props
}) {
  return /* @__PURE__ */ jsx12("nav", { "aria-label": "breadcrumb", "data-slot": "breadcrumb", ...props });
}
function BreadcrumbList({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx12(
    "ol",
    {
      "data-slot": "breadcrumb-list",
      className: cn(
        "text-muted-foreground flex flex-wrap items-center gap-1.5 text-sm break-words sm:gap-2.5",
        className
      ),
      ...props
    }
  );
}
function BreadcrumbItem({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx12(
    "li",
    {
      "data-slot": "breadcrumb-item",
      className: cn("inline-flex items-center gap-1.5", className),
      ...props
    }
  );
}
function BreadcrumbLink({
  asChild,
  className,
  ...props
}) {
  const Comp = asChild ? Slot4 : "a";
  return /* @__PURE__ */ jsx12(
    Comp,
    {
      "data-slot": "breadcrumb-link",
      className: cn("hover:text-foreground transition-colors", className),
      ...props
    }
  );
}
function BreadcrumbPage({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx12(
    "span",
    {
      "data-slot": "breadcrumb-page",
      role: "link",
      "aria-disabled": "true",
      "aria-current": "page",
      className: cn("text-foreground font-normal", className),
      ...props
    }
  );
}
function BreadcrumbSeparator({
  children,
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx12(
    "li",
    {
      "data-slot": "breadcrumb-separator",
      role: "presentation",
      "aria-hidden": "true",
      className: cn("[&>svg]:size-3.5", className),
      ...props,
      children: children ?? /* @__PURE__ */ jsx12(ChevronRight, {})
    }
  );
}
function BreadcrumbEllipsis({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsxs6(
    "span",
    {
      "data-slot": "breadcrumb-ellipsis",
      role: "presentation",
      "aria-hidden": "true",
      className: cn("flex size-9 items-center justify-center", className),
      ...props,
      children: [
        /* @__PURE__ */ jsx12(MoreHorizontal, { className: "size-4" }),
        /* @__PURE__ */ jsx12("span", { className: "sr-only", children: "More" })
      ]
    }
  );
}

// src/components/ui/calendar.tsx
import { ChevronLeft, ChevronRight as ChevronRight2 } from "lucide-react";
import { DayPicker } from "react-day-picker";
import { jsx as jsx13 } from "react/jsx-runtime";
function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}) {
  return /* @__PURE__ */ jsx13(
    DayPicker,
    {
      showOutsideDays,
      className: cn("p-3", className),
      classNames: {
        months: "flex flex-col sm:flex-row gap-2",
        month: "flex flex-col gap-4",
        caption: "flex justify-center pt-1 relative items-center w-full",
        caption_label: "text-sm font-medium",
        nav: "flex items-center gap-1",
        nav_button: cn(
          buttonVariants({ variant: "outline" }),
          "size-7 bg-transparent p-0 opacity-50 hover:opacity-100"
        ),
        nav_button_previous: "absolute left-1",
        nav_button_next: "absolute right-1",
        table: "w-full border-collapse space-x-1",
        head_row: "flex",
        head_cell: "text-muted-foreground rounded-md w-8 font-normal text-[0.8rem]",
        row: "flex w-full mt-2",
        cell: cn(
          "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent [&:has([aria-selected].day-range-end)]:rounded-r-md",
          props.mode === "range" ? "[&:has(>.day-range-end)]:rounded-r-md [&:has(>.day-range-start)]:rounded-l-md first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md" : "[&:has([aria-selected])]:rounded-md"
        ),
        day: cn(
          buttonVariants({ variant: "ghost" }),
          "size-8 p-0 font-normal aria-selected:opacity-100"
        ),
        day_range_start: "day-range-start aria-selected:bg-primary aria-selected:text-primary-foreground",
        day_range_end: "day-range-end aria-selected:bg-primary aria-selected:text-primary-foreground",
        day_selected: "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
        day_today: "bg-accent text-accent-foreground",
        day_outside: "day-outside text-muted-foreground aria-selected:text-muted-foreground",
        day_disabled: "text-muted-foreground opacity-50",
        day_range_middle: "aria-selected:bg-accent aria-selected:text-accent-foreground",
        day_hidden: "invisible",
        ...classNames
      },
      components: {
        Chevron: ({ orientation, ...props2 }) => {
          const Icon2 = orientation === "left" ? ChevronLeft : ChevronRight2;
          return /* @__PURE__ */ jsx13(Icon2, { className: "size-4", ...props2 });
        }
      },
      ...props
    }
  );
}

// src/components/ui/card.tsx
import { jsx as jsx14 } from "react/jsx-runtime";
function Card({
  className,
  interactive = false,
  ...props
}) {
  return /* @__PURE__ */ jsx14(
    "div",
    {
      "data-slot": "card",
      className: cn(
        "bg-white text-neutral-900 flex flex-col gap-4 rounded-xl border border-neutral-200 py-5 shadow-sm font-['Public_Sans',sans-serif] dark:bg-neutral-800 dark:text-neutral-100 dark:border-neutral-700",
        interactive && "card-hover cursor-pointer hover:shadow-md hover:border-primary-200 transition-all",
        className
      ),
      ...props
    }
  );
}
function CardHeader({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx14(
    "div",
    {
      "data-slot": "card-header",
      className: cn(
        "@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-1.5 px-6 has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-6",
        className
      ),
      ...props
    }
  );
}
function CardTitle({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx14(
    "div",
    {
      "data-slot": "card-title",
      className: cn("leading-none font-semibold text-neutral-900 dark:text-neutral-100", className),
      ...props
    }
  );
}
function CardDescription({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx14(
    "div",
    {
      "data-slot": "card-description",
      className: cn("text-neutral-500 text-sm dark:text-neutral-400", className),
      ...props
    }
  );
}
function CardAction({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx14(
    "div",
    {
      "data-slot": "card-action",
      className: cn(
        "col-start-2 row-span-2 row-start-1 self-start justify-self-end",
        className
      ),
      ...props
    }
  );
}
function CardContent({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx14("div", { "data-slot": "card-content", className: cn("px-6", className), ...props });
}
function CardFooter({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx14(
    "div",
    {
      "data-slot": "card-footer",
      className: cn("flex items-center px-6 [.border-t]:pt-6", className),
      ...props
    }
  );
}

// src/components/ui/carousel.tsx
import * as React3 from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { jsx as jsx15, jsxs as jsxs7 } from "react/jsx-runtime";
var CarouselContext = React3.createContext(null);
function useCarousel() {
  const context = React3.useContext(CarouselContext);
  if (!context) {
    throw new Error("useCarousel must be used within a <Carousel />");
  }
  return context;
}
function Carousel({
  orientation = "horizontal",
  opts,
  setApi,
  plugins,
  className,
  children,
  ...props
}) {
  const [carouselRef, api] = useEmblaCarousel({
    ...opts,
    axis: orientation === "horizontal" ? "x" : "y"
  }, plugins);
  const [canScrollPrev, setCanScrollPrev] = React3.useState(false);
  const [canScrollNext, setCanScrollNext] = React3.useState(false);
  const onSelect = React3.useCallback((api2) => {
    if (!api2) return;
    setCanScrollPrev(api2.canScrollPrev());
    setCanScrollNext(api2.canScrollNext());
  }, []);
  const scrollPrev = React3.useCallback(() => {
    api?.scrollPrev();
  }, [api]);
  const scrollNext = React3.useCallback(() => {
    api?.scrollNext();
  }, [api]);
  const handleKeyDown = React3.useCallback((event) => {
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      scrollPrev();
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      scrollNext();
    }
  }, [scrollPrev, scrollNext]);
  React3.useEffect(() => {
    if (!api || !setApi) return;
    setApi(api);
  }, [api, setApi]);
  React3.useEffect(() => {
    if (!api) return;
    onSelect(api);
    api.on("reInit", onSelect);
    api.on("select", onSelect);
    return () => {
      api?.off("select", onSelect);
    };
  }, [api, onSelect]);
  return /* @__PURE__ */ jsx15(
    CarouselContext.Provider,
    {
      value: {
        carouselRef,
        api,
        opts,
        orientation: orientation || (opts?.axis === "y" ? "vertical" : "horizontal"),
        scrollPrev,
        scrollNext,
        canScrollPrev,
        canScrollNext
      },
      children: /* @__PURE__ */ jsx15(
        "div",
        {
          onKeyDownCapture: handleKeyDown,
          className: cn("relative", className),
          role: "region",
          "aria-roledescription": "carousel",
          "data-slot": "carousel",
          ...props,
          children
        }
      )
    }
  );
}
function CarouselContent({
  className,
  ...props
}) {
  const { carouselRef, orientation } = useCarousel();
  return /* @__PURE__ */ jsx15(
    "div",
    {
      ref: carouselRef,
      className: "overflow-hidden",
      "data-slot": "carousel-content",
      children: /* @__PURE__ */ jsx15(
        "div",
        {
          className: cn(
            "flex",
            orientation === "horizontal" ? "-ml-4" : "-mt-4 flex-col",
            className
          ),
          ...props
        }
      )
    }
  );
}
function CarouselItem({
  className,
  ...props
}) {
  const { orientation } = useCarousel();
  return /* @__PURE__ */ jsx15(
    "div",
    {
      role: "group",
      "aria-roledescription": "slide",
      "data-slot": "carousel-item",
      className: cn(
        "min-w-0 shrink-0 grow-0 basis-full",
        orientation === "horizontal" ? "pl-4" : "pt-4",
        className
      ),
      ...props
    }
  );
}
function CarouselPrevious({
  className,
  variant = "outline",
  size = "icon",
  ...props
}) {
  const { orientation, scrollPrev, canScrollPrev } = useCarousel();
  return /* @__PURE__ */ jsxs7(
    Button,
    {
      "data-slot": "carousel-previous",
      variant,
      size,
      className: cn("absolute size-8 rounded-full", orientation === "horizontal" ? "top-1/2 -left-12 -translate-y-1/2" : "-top-12 left-1/2 -translate-x-1/2 rotate-90", className),
      disabled: !canScrollPrev,
      onClick: scrollPrev,
      ...props,
      children: [
        /* @__PURE__ */ jsx15(ArrowLeft, {}),
        /* @__PURE__ */ jsx15("span", { className: "sr-only", children: "Previous slide" })
      ]
    }
  );
}
function CarouselNext({
  className,
  variant = "outline",
  size = "icon",
  ...props
}) {
  const { orientation, scrollNext, canScrollNext } = useCarousel();
  return /* @__PURE__ */ jsxs7(
    Button,
    {
      "data-slot": "carousel-next",
      variant,
      size,
      className: cn("absolute size-8 rounded-full", orientation === "horizontal" ? "top-1/2 -right-12 -translate-y-1/2" : "-bottom-12 left-1/2 -translate-x-1/2 rotate-90", className),
      disabled: !canScrollNext,
      onClick: scrollNext,
      ...props,
      children: [
        /* @__PURE__ */ jsx15(ArrowRight, {}),
        /* @__PURE__ */ jsx15("span", { className: "sr-only", children: "Next slide" })
      ]
    }
  );
}

// src/components/ui/chart.tsx
import * as React4 from "react";
import * as RechartsPrimitive from "recharts";
import { Fragment, jsx as jsx16, jsxs as jsxs8 } from "react/jsx-runtime";
var THEMES = {
  light: "",
  dark: ".dark"
};
var ChartContext = React4.createContext(null);
function useChart() {
  const context = React4.useContext(ChartContext);
  if (!context) {
    throw new Error("useChart must be used within a <ChartContainer />");
  }
  return context;
}
function ChartContainer({
  id,
  className,
  children,
  config,
  ...props
}) {
  const uniqueId = React4.useId();
  const chartId = `chart-${id || uniqueId.replace(/:/g, "")}`;
  return /* @__PURE__ */ jsx16(ChartContext.Provider, { value: { config }, children: /* @__PURE__ */ jsxs8(
    "div",
    {
      "data-slot": "chart",
      "data-chart": chartId,
      className: cn(
        "[&_.recharts-cartesian-axis-tick_text]:fill-muted-foreground [&_.recharts-cartesian-grid_line[stroke='#ccc']]:stroke-border/50 [&_.recharts-curve.recharts-tooltip-cursor]:stroke-border [&_.recharts-polar-grid_[stroke='#ccc']]:stroke-border [&_.recharts-radial-bar-background-sector]:fill-muted [&_.recharts-rectangle.recharts-tooltip-cursor]:fill-muted [&_.recharts-reference-line_[stroke='#ccc']]:stroke-border flex aspect-video justify-center text-xs [&_.recharts-dot[stroke='#fff']]:stroke-transparent [&_.recharts-layer]:outline-hidden [&_.recharts-sector]:outline-hidden [&_.recharts-sector[stroke='#fff']]:stroke-transparent [&_.recharts-surface]:outline-hidden",
        className
      ),
      ...props,
      children: [
        /* @__PURE__ */ jsx16(ChartStyle, { id: chartId, config }),
        /* @__PURE__ */ jsx16(RechartsPrimitive.ResponsiveContainer, { children })
      ]
    }
  ) });
}
var ChartStyle = ({
  id,
  config
}) => {
  const colorConfig = Object.entries(config).filter(([, config2]) => config2.theme || config2.color);
  if (!colorConfig.length) {
    return null;
  }
  return /* @__PURE__ */ jsx16(
    "style",
    {
      dangerouslySetInnerHTML: {
        __html: Object.entries(THEMES).map(([theme, prefix]) => `
${prefix} [data-chart=${id}] {
${colorConfig.map(([key, itemConfig]) => {
          const color = itemConfig.theme?.[theme] || itemConfig.color;
          return color ? `  --color-${key}: ${color};` : null;
        }).join("\n")}
}
`).join("\n")
      }
    }
  );
};
var ChartTooltip = RechartsPrimitive.Tooltip;
function ChartTooltipContent({
  active,
  payload,
  className,
  indicator = "dot",
  hideLabel = false,
  hideIndicator = false,
  label,
  labelFormatter,
  labelClassName,
  formatter,
  color,
  nameKey,
  labelKey
}) {
  const { config } = useChart();
  const tooltipLabel = React4.useMemo(() => {
    if (hideLabel || !payload?.length) {
      return null;
    }
    const [item] = payload;
    const key = `${labelKey || item?.dataKey || item?.name || "value"}`;
    const itemConfig = getPayloadConfigFromPayload(config, item, key);
    const value = !labelKey && typeof label === "string" ? config[label]?.label || label : itemConfig?.label;
    if (labelFormatter) {
      return /* @__PURE__ */ jsx16("div", { className: cn("font-medium", labelClassName), children: labelFormatter(value, payload) });
    }
    if (!value) {
      return null;
    }
    return /* @__PURE__ */ jsx16("div", { className: cn("font-medium", labelClassName), children: value });
  }, [
    label,
    labelFormatter,
    payload,
    hideLabel,
    labelClassName,
    config,
    labelKey
  ]);
  if (!active || !payload?.length) {
    return null;
  }
  const nestLabel = payload.length === 1 && indicator !== "dot";
  return /* @__PURE__ */ jsxs8(
    "div",
    {
      className: cn(
        "border-border/50 bg-background grid min-w-[8rem] items-start gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs shadow-xl",
        className
      ),
      children: [
        !nestLabel ? tooltipLabel : null,
        /* @__PURE__ */ jsx16("div", { className: "grid gap-1.5", children: payload.map((item, index) => {
          const key = `${nameKey || item.name || item.dataKey || "value"}`;
          const itemConfig = getPayloadConfigFromPayload(config, item, key);
          const indicatorColor = color || item.payload.fill || item.color;
          return /* @__PURE__ */ jsx16(
            "div",
            {
              className: cn(
                "[&>svg]:text-muted-foreground flex w-full flex-wrap items-stretch gap-2 [&>svg]:h-2.5 [&>svg]:w-2.5",
                indicator === "dot" && "items-center"
              ),
              children: formatter && item?.value !== void 0 && item.name ? formatter(item.value, item.name, item, index, item.payload) : /* @__PURE__ */ jsxs8(Fragment, { children: [
                itemConfig?.icon ? /* @__PURE__ */ jsx16(itemConfig.icon, {}) : !hideIndicator && /* @__PURE__ */ jsx16(
                  "div",
                  {
                    className: cn("shrink-0 rounded-[2px] border-(--color-border) bg-(--color-bg)", {
                      "h-2.5 w-2.5": indicator === "dot",
                      "w-1": indicator === "line",
                      "w-0 border-[1.5px] border-dashed bg-transparent": indicator === "dashed",
                      "my-0.5": nestLabel && indicator === "dashed"
                    }),
                    style: {
                      "--color-bg": indicatorColor,
                      "--color-border": indicatorColor
                    }
                  }
                ),
                /* @__PURE__ */ jsxs8(
                  "div",
                  {
                    className: cn(
                      "flex flex-1 justify-between leading-none",
                      nestLabel ? "items-end" : "items-center"
                    ),
                    children: [
                      /* @__PURE__ */ jsxs8("div", { className: "grid gap-1.5", children: [
                        nestLabel ? tooltipLabel : null,
                        /* @__PURE__ */ jsx16("span", { className: "text-muted-foreground", children: itemConfig?.label || item.name })
                      ] }),
                      item.value && /* @__PURE__ */ jsx16("span", { className: "text-foreground font-mono font-medium tabular-nums", children: item.value.toLocaleString() })
                    ]
                  }
                )
              ] })
            },
            item.dataKey
          );
        }) })
      ]
    }
  );
}
var ChartLegend = RechartsPrimitive.Legend;
function ChartLegendContent({
  className,
  hideIcon = false,
  payload,
  verticalAlign = "bottom",
  nameKey
}) {
  const { config } = useChart();
  if (!payload?.length) {
    return null;
  }
  return /* @__PURE__ */ jsx16(
    "div",
    {
      className: cn(
        "flex items-center justify-center gap-4",
        verticalAlign === "top" ? "pb-3" : "pt-3",
        className
      ),
      children: payload.map((item) => {
        const key = `${nameKey || item.dataKey || "value"}`;
        const itemConfig = getPayloadConfigFromPayload(config, item, key);
        return /* @__PURE__ */ jsxs8(
          "div",
          {
            className: cn(
              "[&>svg]:text-muted-foreground flex items-center gap-1.5 [&>svg]:h-3 [&>svg]:w-3"
            ),
            children: [
              itemConfig?.icon && !hideIcon ? /* @__PURE__ */ jsx16(itemConfig.icon, {}) : /* @__PURE__ */ jsx16(
                "div",
                {
                  className: "h-2 w-2 shrink-0 rounded-[2px]",
                  style: {
                    backgroundColor: item.color
                  }
                }
              ),
              itemConfig?.label
            ]
          },
          item.value
        );
      })
    }
  );
}
function getPayloadConfigFromPayload(config, payload, key) {
  if (typeof payload !== "object" || payload === null) {
    return void 0;
  }
  const payloadPayload = "payload" in payload && typeof payload.payload === "object" && payload.payload !== null ? payload.payload : void 0;
  let configLabelKey = key;
  if (key in payload && typeof payload[key] === "string") {
    configLabelKey = payload[key];
  } else if (payloadPayload && key in payloadPayload && typeof payloadPayload[key] === "string") {
    configLabelKey = payloadPayload[key];
  }
  return configLabelKey in config ? config[configLabelKey] : config[key];
}

// src/components/ui/checkbox.tsx
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { CheckIcon } from "lucide-react";
import { jsx as jsx17 } from "react/jsx-runtime";
function Checkbox({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx17(
    CheckboxPrimitive.Root,
    {
      "data-slot": "checkbox",
      className: cn(
        "border-input data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 peer size-4 shrink-0 rounded-xs border shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:border-transparent",
        className
      ),
      ...props,
      children: /* @__PURE__ */ jsx17(
        CheckboxPrimitive.Indicator,
        {
          "data-slot": "checkbox-indicator",
          className: cn("flex items-center justify-center text-current"),
          children: /* @__PURE__ */ jsx17(CheckIcon, { className: "size-4" })
        }
      )
    }
  );
}

// src/components/ui/collapsible.tsx
import * as CollapsiblePrimitive from "@radix-ui/react-collapsible";
import { jsx as jsx18 } from "react/jsx-runtime";
function Collapsible({
  ...props
}) {
  return /* @__PURE__ */ jsx18(CollapsiblePrimitive.Root, { "data-slot": "collapsible", ...props });
}
function CollapsibleTrigger2({
  ...props
}) {
  return /* @__PURE__ */ jsx18(CollapsiblePrimitive.CollapsibleTrigger, { "data-slot": "collapsible-trigger", ...props });
}
function CollapsibleContent2({
  ...props
}) {
  return /* @__PURE__ */ jsx18(CollapsiblePrimitive.CollapsibleContent, { "data-slot": "collapsible-content", ...props });
}

// src/components/ui/command.tsx
import { Command as CommandPrimitive } from "cmdk";
import { SearchIcon } from "lucide-react";

// src/components/ui/dialog.tsx
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { XIcon } from "lucide-react";
import { jsx as jsx19, jsxs as jsxs9 } from "react/jsx-runtime";
function Dialog({
  ...props
}) {
  return /* @__PURE__ */ jsx19(DialogPrimitive.Root, { "data-slot": "dialog", ...props });
}
function DialogTrigger({
  ...props
}) {
  return /* @__PURE__ */ jsx19(DialogPrimitive.Trigger, { "data-slot": "dialog-trigger", ...props });
}
function DialogPortal({
  ...props
}) {
  return /* @__PURE__ */ jsx19(DialogPrimitive.Portal, { "data-slot": "dialog-portal", ...props });
}
function DialogClose({
  ...props
}) {
  return /* @__PURE__ */ jsx19(DialogPrimitive.Close, { "data-slot": "dialog-close", ...props });
}
function DialogOverlay({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx19(
    DialogPrimitive.Overlay,
    {
      "data-slot": "dialog-overlay",
      className: cn(
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-[9999] bg-black/50",
        className
      ),
      ...props
    }
  );
}
function DialogContent({
  className,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsxs9(DialogPortal, { "data-slot": "dialog-portal", children: [
    /* @__PURE__ */ jsx19(DialogOverlay, {}),
    /* @__PURE__ */ jsxs9(
      DialogPrimitive.Content,
      {
        "data-slot": "dialog-content",
        className: cn(
          "bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[50%] z-[10000] grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border p-6 shadow-lg duration-200 sm:max-w-lg",
          className
        ),
        ...props,
        children: [
          children,
          /* @__PURE__ */ jsxs9(
            DialogPrimitive.Close,
            {
              className: "ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
              children: [
                /* @__PURE__ */ jsx19(XIcon, {}),
                /* @__PURE__ */ jsx19("span", { className: "sr-only", children: "Close" })
              ]
            }
          )
        ]
      }
    )
  ] });
}
function DialogHeader({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx19(
    "div",
    {
      "data-slot": "dialog-header",
      className: cn("flex flex-col gap-2 text-center sm:text-left", className),
      ...props
    }
  );
}
function DialogFooter({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx19(
    "div",
    {
      "data-slot": "dialog-footer",
      className: cn("flex flex-col-reverse gap-2 sm:flex-row sm:justify-end", className),
      ...props
    }
  );
}
function DialogTitle({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx19(
    DialogPrimitive.Title,
    {
      "data-slot": "dialog-title",
      className: cn("text-lg leading-none font-semibold", className),
      ...props
    }
  );
}
function DialogDescription({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx19(
    DialogPrimitive.Description,
    {
      "data-slot": "dialog-description",
      className: cn("text-muted-foreground text-sm", className),
      ...props
    }
  );
}

// src/components/ui/command.tsx
import { jsx as jsx20, jsxs as jsxs10 } from "react/jsx-runtime";
function Command({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx20(
    CommandPrimitive,
    {
      "data-slot": "command",
      className: cn(
        "bg-popover text-popover-foreground flex h-full w-full flex-col overflow-hidden rounded-md",
        className
      ),
      ...props
    }
  );
}
function CommandDialog({
  title = "Command Palette",
  description = "Search for a command to run...",
  children,
  ...props
}) {
  return /* @__PURE__ */ jsxs10(Dialog, { ...props, children: [
    /* @__PURE__ */ jsxs10(DialogHeader, { className: "sr-only", children: [
      /* @__PURE__ */ jsx20(DialogTitle, { children: title }),
      /* @__PURE__ */ jsx20(DialogDescription, { children: description })
    ] }),
    /* @__PURE__ */ jsx20(DialogContent, { className: "overflow-hidden p-0", children: /* @__PURE__ */ jsx20(
      Command,
      {
        className: "[&_[cmdk-group-heading]]:text-muted-foreground **:data-[slot=command-input-wrapper]:h-12 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group]]:px-2 [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5",
        children
      }
    ) })
  ] });
}
function CommandInput({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsxs10(
    "div",
    {
      "data-slot": "command-input-wrapper",
      className: "flex h-9 items-center gap-2 border-b px-3",
      children: [
        /* @__PURE__ */ jsx20(SearchIcon, { className: "size-4 shrink-0 opacity-50" }),
        /* @__PURE__ */ jsx20(
          CommandPrimitive.Input,
          {
            "data-slot": "command-input",
            className: cn(
              "placeholder:text-muted-foreground flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-hidden disabled:cursor-not-allowed disabled:opacity-50",
              className
            ),
            ...props
          }
        )
      ]
    }
  );
}
function CommandList({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx20(
    CommandPrimitive.List,
    {
      "data-slot": "command-list",
      className: cn("max-h-[300px] scroll-py-1 overflow-x-hidden overflow-y-auto", className),
      ...props
    }
  );
}
function CommandEmpty({
  ...props
}) {
  return /* @__PURE__ */ jsx20(CommandPrimitive.Empty, { "data-slot": "command-empty", className: "py-6 text-center text-sm", ...props });
}
function CommandGroup({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx20(
    CommandPrimitive.Group,
    {
      "data-slot": "command-group",
      className: cn(
        "text-foreground [&_[cmdk-group-heading]]:text-muted-foreground overflow-hidden p-1 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium",
        className
      ),
      ...props
    }
  );
}
function CommandSeparator({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx20(
    CommandPrimitive.Separator,
    {
      "data-slot": "command-separator",
      className: cn("bg-border -mx-1 h-px", className),
      ...props
    }
  );
}
function CommandItem({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx20(
    CommandPrimitive.Item,
    {
      "data-slot": "command-item",
      className: cn(
        "data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground [&_svg:not([class*='text-'])]:text-muted-foreground relative flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      ),
      ...props
    }
  );
}
function CommandShortcut({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx20(
    "span",
    {
      "data-slot": "command-shortcut",
      className: cn("text-muted-foreground ml-auto text-xs tracking-widest", className),
      ...props
    }
  );
}

// src/components/ui/context-menu.tsx
import * as ContextMenuPrimitive from "@radix-ui/react-context-menu";
import { CheckIcon as CheckIcon2, ChevronRightIcon, CircleIcon } from "lucide-react";
import { jsx as jsx21, jsxs as jsxs11 } from "react/jsx-runtime";
function ContextMenu({
  ...props
}) {
  return /* @__PURE__ */ jsx21(ContextMenuPrimitive.Root, { "data-slot": "context-menu", ...props });
}
function ContextMenuTrigger({
  ...props
}) {
  return /* @__PURE__ */ jsx21(ContextMenuPrimitive.Trigger, { "data-slot": "context-menu-trigger", ...props });
}
function ContextMenuGroup({
  ...props
}) {
  return /* @__PURE__ */ jsx21(ContextMenuPrimitive.Group, { "data-slot": "context-menu-group", ...props });
}
function ContextMenuPortal({
  ...props
}) {
  return /* @__PURE__ */ jsx21(ContextMenuPrimitive.Portal, { "data-slot": "context-menu-portal", ...props });
}
function ContextMenuSub({
  ...props
}) {
  return /* @__PURE__ */ jsx21(ContextMenuPrimitive.Sub, { "data-slot": "context-menu-sub", ...props });
}
function ContextMenuRadioGroup({
  ...props
}) {
  return /* @__PURE__ */ jsx21(ContextMenuPrimitive.RadioGroup, { "data-slot": "context-menu-radio-group", ...props });
}
function ContextMenuSubTrigger({
  className,
  inset,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsxs11(
    ContextMenuPrimitive.SubTrigger,
    {
      "data-slot": "context-menu-sub-trigger",
      "data-inset": inset,
      className: cn(
        "focus:bg-accent focus:text-accent-foreground data-[state=open]:bg-accent data-[state=open]:text-accent-foreground flex cursor-default items-center rounded-sm px-2 py-1.5 text-sm outline-hidden select-none data-[inset]:pl-8 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      ),
      ...props,
      children: [
        children,
        /* @__PURE__ */ jsx21(ChevronRightIcon, { className: "ml-auto" })
      ]
    }
  );
}
function ContextMenuSubContent({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx21(
    ContextMenuPrimitive.SubContent,
    {
      "data-slot": "context-menu-sub-content",
      className: cn(
        "bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 min-w-[8rem] origin-(--radix-context-menu-content-transform-origin) overflow-hidden rounded-md border p-1 shadow-lg",
        className
      ),
      ...props
    }
  );
}
function ContextMenuContent({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx21(ContextMenuPrimitive.Portal, { children: /* @__PURE__ */ jsx21(
    ContextMenuPrimitive.Content,
    {
      "data-slot": "context-menu-content",
      className: cn(
        "bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 max-h-(--radix-context-menu-content-available-height) min-w-[8rem] origin-(--radix-context-menu-content-transform-origin) overflow-x-hidden overflow-y-auto rounded-md border p-1 shadow-md",
        className
      ),
      ...props
    }
  ) });
}
function ContextMenuItem({
  className,
  inset,
  variant = "default",
  ...props
}) {
  return /* @__PURE__ */ jsx21(
    ContextMenuPrimitive.Item,
    {
      "data-slot": "context-menu-item",
      "data-inset": inset,
      "data-variant": variant,
      className: cn(
        "focus:bg-accent focus:text-accent-foreground data-[variant=destructive]:text-destructive data-[variant=destructive]:focus:bg-destructive/10 dark:data-[variant=destructive]:focus:bg-destructive/20 data-[variant=destructive]:focus:text-destructive data-[variant=destructive]:*:[svg]:!text-destructive [&_svg:not([class*='text-'])]:text-muted-foreground relative flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[inset]:pl-8 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      ),
      ...props
    }
  );
}
function ContextMenuCheckboxItem({
  className,
  children,
  checked,
  ...props
}) {
  return /* @__PURE__ */ jsxs11(
    ContextMenuPrimitive.CheckboxItem,
    {
      "data-slot": "context-menu-checkbox-item",
      className: cn(
        "focus:bg-accent focus:text-accent-foreground relative flex cursor-default items-center gap-2 rounded-sm py-1.5 pr-2 pl-8 text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      ),
      checked,
      ...props,
      children: [
        /* @__PURE__ */ jsx21(
          "span",
          {
            className: "pointer-events-none absolute left-2 flex size-3.5 items-center justify-center",
            children: /* @__PURE__ */ jsx21(ContextMenuPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx21(CheckIcon2, { className: "size-4" }) })
          }
        ),
        children
      ]
    }
  );
}
function ContextMenuRadioItem({
  className,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsxs11(
    ContextMenuPrimitive.RadioItem,
    {
      "data-slot": "context-menu-radio-item",
      className: cn(
        "focus:bg-accent focus:text-accent-foreground relative flex cursor-default items-center gap-2 rounded-sm py-1.5 pr-2 pl-8 text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      ),
      ...props,
      children: [
        /* @__PURE__ */ jsx21(
          "span",
          {
            className: "pointer-events-none absolute left-2 flex size-3.5 items-center justify-center",
            children: /* @__PURE__ */ jsx21(ContextMenuPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx21(CircleIcon, { className: "size-2 fill-current" }) })
          }
        ),
        children
      ]
    }
  );
}
function ContextMenuLabel({
  className,
  inset,
  ...props
}) {
  return /* @__PURE__ */ jsx21(
    ContextMenuPrimitive.Label,
    {
      "data-slot": "context-menu-label",
      "data-inset": inset,
      className: cn(
        "text-foreground px-2 py-1.5 text-sm font-medium data-[inset]:pl-8",
        className
      ),
      ...props
    }
  );
}
function ContextMenuSeparator({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx21(
    ContextMenuPrimitive.Separator,
    {
      "data-slot": "context-menu-separator",
      className: cn("bg-border -mx-1 my-1 h-px", className),
      ...props
    }
  );
}
function ContextMenuShortcut({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx21(
    "span",
    {
      "data-slot": "context-menu-shortcut",
      className: cn("text-muted-foreground ml-auto text-xs tracking-widest", className),
      ...props
    }
  );
}

// src/components/ui/drawer.tsx
import { Drawer as DrawerPrimitive } from "vaul";
import { jsx as jsx22, jsxs as jsxs12 } from "react/jsx-runtime";
function Drawer({
  ...props
}) {
  return /* @__PURE__ */ jsx22(DrawerPrimitive.Root, { "data-slot": "drawer", ...props });
}
function DrawerTrigger({
  ...props
}) {
  return /* @__PURE__ */ jsx22(DrawerPrimitive.Trigger, { "data-slot": "drawer-trigger", ...props });
}
function DrawerPortal({
  ...props
}) {
  return /* @__PURE__ */ jsx22(DrawerPrimitive.Portal, { "data-slot": "drawer-portal", ...props });
}
function DrawerClose({
  ...props
}) {
  return /* @__PURE__ */ jsx22(DrawerPrimitive.Close, { "data-slot": "drawer-close", ...props });
}
function DrawerOverlay({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx22(
    DrawerPrimitive.Overlay,
    {
      "data-slot": "drawer-overlay",
      className: cn(
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50",
        className
      ),
      ...props
    }
  );
}
function DrawerContent({
  className,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsxs12(DrawerPortal, { "data-slot": "drawer-portal", children: [
    /* @__PURE__ */ jsx22(DrawerOverlay, {}),
    /* @__PURE__ */ jsxs12(
      DrawerPrimitive.Content,
      {
        "data-slot": "drawer-content",
        className: cn(
          "group/drawer-content bg-background fixed z-50 flex h-auto flex-col",
          "data-[vaul-drawer-direction=top]:inset-x-0 data-[vaul-drawer-direction=top]:top-0 data-[vaul-drawer-direction=top]:mb-24 data-[vaul-drawer-direction=top]:max-h-[80vh] data-[vaul-drawer-direction=top]:rounded-b-lg data-[vaul-drawer-direction=top]:border-b",
          "data-[vaul-drawer-direction=bottom]:inset-x-0 data-[vaul-drawer-direction=bottom]:bottom-0 data-[vaul-drawer-direction=bottom]:mt-24 data-[vaul-drawer-direction=bottom]:max-h-[80vh] data-[vaul-drawer-direction=bottom]:rounded-t-lg data-[vaul-drawer-direction=bottom]:border-t",
          "data-[vaul-drawer-direction=right]:inset-y-0 data-[vaul-drawer-direction=right]:right-0 data-[vaul-drawer-direction=right]:w-3/4 data-[vaul-drawer-direction=right]:border-l data-[vaul-drawer-direction=right]:sm:max-w-sm",
          "data-[vaul-drawer-direction=left]:inset-y-0 data-[vaul-drawer-direction=left]:left-0 data-[vaul-drawer-direction=left]:w-3/4 data-[vaul-drawer-direction=left]:border-r data-[vaul-drawer-direction=left]:sm:max-w-sm",
          className
        ),
        ...props,
        children: [
          /* @__PURE__ */ jsx22(
            "div",
            {
              className: "bg-muted mx-auto mt-4 hidden h-2 w-[100px] shrink-0 rounded-full group-data-[vaul-drawer-direction=bottom]/drawer-content:block"
            }
          ),
          children
        ]
      }
    )
  ] });
}
function DrawerHeader({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx22(
    "div",
    {
      "data-slot": "drawer-header",
      className: cn("flex flex-col gap-1.5 p-4", className),
      ...props
    }
  );
}
function DrawerFooter({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx22(
    "div",
    {
      "data-slot": "drawer-footer",
      className: cn("mt-auto flex flex-col gap-2 p-4", className),
      ...props
    }
  );
}
function DrawerTitle({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx22(
    DrawerPrimitive.Title,
    {
      "data-slot": "drawer-title",
      className: cn("text-foreground font-semibold", className),
      ...props
    }
  );
}
function DrawerDescription({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx22(
    DrawerPrimitive.Description,
    {
      "data-slot": "drawer-description",
      className: cn("text-muted-foreground text-sm", className),
      ...props
    }
  );
}

// src/components/ui/dropdown-menu.tsx
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { CheckIcon as CheckIcon3, ChevronRightIcon as ChevronRightIcon2, CircleIcon as CircleIcon2 } from "lucide-react";
import { jsx as jsx23, jsxs as jsxs13 } from "react/jsx-runtime";
function DropdownMenu({
  ...props
}) {
  return /* @__PURE__ */ jsx23(DropdownMenuPrimitive.Root, { "data-slot": "dropdown-menu", ...props });
}
function DropdownMenuPortal({
  ...props
}) {
  return /* @__PURE__ */ jsx23(DropdownMenuPrimitive.Portal, { "data-slot": "dropdown-menu-portal", ...props });
}
function DropdownMenuTrigger({
  ...props
}) {
  return /* @__PURE__ */ jsx23(DropdownMenuPrimitive.Trigger, { "data-slot": "dropdown-menu-trigger", ...props });
}
function DropdownMenuContent({
  className,
  sideOffset = 4,
  ...props
}) {
  return /* @__PURE__ */ jsx23(DropdownMenuPrimitive.Portal, { children: /* @__PURE__ */ jsx23(
    DropdownMenuPrimitive.Content,
    {
      "data-slot": "dropdown-menu-content",
      sideOffset,
      className: cn(
        "bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 max-h-(--radix-dropdown-menu-content-available-height) min-w-[8rem] origin-(--radix-dropdown-menu-content-transform-origin) overflow-x-hidden overflow-y-auto rounded-md border p-1 shadow-md",
        className
      ),
      ...props
    }
  ) });
}
function DropdownMenuGroup({
  ...props
}) {
  return /* @__PURE__ */ jsx23(DropdownMenuPrimitive.Group, { "data-slot": "dropdown-menu-group", ...props });
}
function DropdownMenuItem({
  className,
  inset,
  variant = "default",
  ...props
}) {
  return /* @__PURE__ */ jsx23(
    DropdownMenuPrimitive.Item,
    {
      "data-slot": "dropdown-menu-item",
      "data-inset": inset,
      "data-variant": variant,
      className: cn(
        "focus:bg-accent focus:text-accent-foreground data-[variant=destructive]:text-destructive data-[variant=destructive]:focus:bg-destructive/10 dark:data-[variant=destructive]:focus:bg-destructive/20 data-[variant=destructive]:focus:text-destructive data-[variant=destructive]:*:[svg]:!text-destructive [&_svg:not([class*='text-'])]:text-muted-foreground relative flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[inset]:pl-8 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      ),
      ...props
    }
  );
}
function DropdownMenuCheckboxItem({
  className,
  children,
  checked,
  ...props
}) {
  return /* @__PURE__ */ jsxs13(
    DropdownMenuPrimitive.CheckboxItem,
    {
      "data-slot": "dropdown-menu-checkbox-item",
      className: cn(
        "focus:bg-accent focus:text-accent-foreground relative flex cursor-default items-center gap-2 rounded-sm py-1.5 pr-2 pl-8 text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      ),
      checked,
      ...props,
      children: [
        /* @__PURE__ */ jsx23(
          "span",
          {
            className: "pointer-events-none absolute left-2 flex size-3.5 items-center justify-center",
            children: /* @__PURE__ */ jsx23(DropdownMenuPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx23(CheckIcon3, { className: "size-4" }) })
          }
        ),
        children
      ]
    }
  );
}
function DropdownMenuRadioGroup({
  ...props
}) {
  return /* @__PURE__ */ jsx23(DropdownMenuPrimitive.RadioGroup, { "data-slot": "dropdown-menu-radio-group", ...props });
}
function DropdownMenuRadioItem({
  className,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsxs13(
    DropdownMenuPrimitive.RadioItem,
    {
      "data-slot": "dropdown-menu-radio-item",
      className: cn(
        "focus:bg-accent focus:text-accent-foreground relative flex cursor-default items-center gap-2 rounded-sm py-1.5 pr-2 pl-8 text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      ),
      ...props,
      children: [
        /* @__PURE__ */ jsx23(
          "span",
          {
            className: "pointer-events-none absolute left-2 flex size-3.5 items-center justify-center",
            children: /* @__PURE__ */ jsx23(DropdownMenuPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx23(CircleIcon2, { className: "size-2 fill-current" }) })
          }
        ),
        children
      ]
    }
  );
}
function DropdownMenuLabel({
  className,
  inset,
  ...props
}) {
  return /* @__PURE__ */ jsx23(
    DropdownMenuPrimitive.Label,
    {
      "data-slot": "dropdown-menu-label",
      "data-inset": inset,
      className: cn("px-2 py-1.5 text-sm font-medium data-[inset]:pl-8", className),
      ...props
    }
  );
}
function DropdownMenuSeparator({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx23(
    DropdownMenuPrimitive.Separator,
    {
      "data-slot": "dropdown-menu-separator",
      className: cn("bg-border -mx-1 my-1 h-px", className),
      ...props
    }
  );
}
function DropdownMenuShortcut({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx23(
    "span",
    {
      "data-slot": "dropdown-menu-shortcut",
      className: cn("text-muted-foreground ml-auto text-xs tracking-widest", className),
      ...props
    }
  );
}
function DropdownMenuSub({
  ...props
}) {
  return /* @__PURE__ */ jsx23(DropdownMenuPrimitive.Sub, { "data-slot": "dropdown-menu-sub", ...props });
}
function DropdownMenuSubTrigger({
  className,
  inset,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsxs13(
    DropdownMenuPrimitive.SubTrigger,
    {
      "data-slot": "dropdown-menu-sub-trigger",
      "data-inset": inset,
      className: cn(
        "focus:bg-accent focus:text-accent-foreground data-[state=open]:bg-accent data-[state=open]:text-accent-foreground flex cursor-default items-center rounded-sm px-2 py-1.5 text-sm outline-hidden select-none data-[inset]:pl-8",
        className
      ),
      ...props,
      children: [
        children,
        /* @__PURE__ */ jsx23(ChevronRightIcon2, { className: "ml-auto size-4" })
      ]
    }
  );
}
function DropdownMenuSubContent({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx23(
    DropdownMenuPrimitive.SubContent,
    {
      "data-slot": "dropdown-menu-sub-content",
      className: cn(
        "bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 min-w-[8rem] origin-(--radix-dropdown-menu-content-transform-origin) overflow-hidden rounded-md border p-1 shadow-lg",
        className
      ),
      ...props
    }
  );
}

// src/components/ui/form.tsx
import * as React5 from "react";
import { Slot as Slot5 } from "@radix-ui/react-slot";
import { Controller, FormProvider, useFormContext, useFormState } from "react-hook-form";

// src/components/ui/label.tsx
import * as LabelPrimitive from "@radix-ui/react-label";
import { jsx as jsx24 } from "react/jsx-runtime";
function Label3({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx24(
    LabelPrimitive.Root,
    {
      "data-slot": "label",
      className: cn(
        "flex items-center gap-2 text-sm leading-none font-medium select-none group-data-[disabled=true]:pointer-events-none group-data-[disabled=true]:opacity-50 peer-disabled:cursor-not-allowed peer-disabled:opacity-50",
        className
      ),
      ...props
    }
  );
}

// src/components/ui/form.tsx
import { jsx as jsx25 } from "react/jsx-runtime";
var Form = FormProvider;
var FormFieldContext = React5.createContext(
  {}
);
var FormField = (props) => {
  return /* @__PURE__ */ jsx25(FormFieldContext.Provider, { value: { name: props.name }, children: /* @__PURE__ */ jsx25(Controller, { ...props }) });
};
var useFormField = () => {
  const fieldContext = React5.useContext(FormFieldContext);
  const itemContext = React5.useContext(FormItemContext);
  const { getFieldState } = useFormContext();
  const formState = useFormState({ name: fieldContext.name });
  const fieldState = getFieldState(fieldContext.name, formState);
  if (!fieldContext) {
    throw new Error("useFormField should be used within <FormField>");
  }
  const { id } = itemContext;
  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState
  };
};
var FormItemContext = React5.createContext(
  {}
);
function FormItem({
  className,
  ...props
}) {
  const id = React5.useId();
  return /* @__PURE__ */ jsx25(FormItemContext.Provider, { value: { id }, children: /* @__PURE__ */ jsx25("div", { "data-slot": "form-item", className: cn("grid gap-2", className), ...props }) });
}
function FormLabel({
  className,
  ...props
}) {
  const { error, formItemId } = useFormField();
  return /* @__PURE__ */ jsx25(
    Label3,
    {
      "data-slot": "form-label",
      "data-error": !!error,
      className: cn("data-[error=true]:text-destructive", className),
      htmlFor: formItemId,
      ...props
    }
  );
}
function FormControl({
  ...props
}) {
  const { error, formItemId, formDescriptionId, formMessageId } = useFormField();
  return /* @__PURE__ */ jsx25(
    Slot5,
    {
      "data-slot": "form-control",
      id: formItemId,
      "aria-describedby": !error ? `${formDescriptionId}` : `${formDescriptionId} ${formMessageId}`,
      "aria-invalid": !!error,
      ...props
    }
  );
}
function FormDescription({
  className,
  ...props
}) {
  const { formDescriptionId } = useFormField();
  return /* @__PURE__ */ jsx25(
    "p",
    {
      "data-slot": "form-description",
      id: formDescriptionId,
      className: cn("text-muted-foreground text-sm", className),
      ...props
    }
  );
}
function FormMessage({
  className,
  ...props
}) {
  const { error, formMessageId } = useFormField();
  const body = error ? String(error?.message ?? "") : props.children;
  if (!body) {
    return null;
  }
  return /* @__PURE__ */ jsx25(
    "p",
    {
      "data-slot": "form-message",
      id: formMessageId,
      className: cn("text-destructive text-sm", className),
      ...props,
      children: body
    }
  );
}

// src/components/ui/hover-card.tsx
import * as HoverCardPrimitive from "@radix-ui/react-hover-card";
import { jsx as jsx26 } from "react/jsx-runtime";
function HoverCard({
  ...props
}) {
  return /* @__PURE__ */ jsx26(HoverCardPrimitive.Root, { "data-slot": "hover-card", ...props });
}
function HoverCardTrigger({
  ...props
}) {
  return /* @__PURE__ */ jsx26(HoverCardPrimitive.Trigger, { "data-slot": "hover-card-trigger", ...props });
}
function HoverCardContent({
  className,
  align = "center",
  sideOffset = 4,
  ...props
}) {
  return /* @__PURE__ */ jsx26(HoverCardPrimitive.Portal, { "data-slot": "hover-card-portal", children: /* @__PURE__ */ jsx26(
    HoverCardPrimitive.Content,
    {
      "data-slot": "hover-card-content",
      align,
      sideOffset,
      className: cn(
        "bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 w-64 origin-(--radix-hover-card-content-transform-origin) rounded-md border p-4 shadow-md outline-hidden",
        className
      ),
      ...props
    }
  ) });
}

// src/components/ui/input-otp.tsx
import * as React6 from "react";
import { OTPInput, OTPInputContext } from "input-otp";
import { MinusIcon } from "lucide-react";
import { jsx as jsx27, jsxs as jsxs14 } from "react/jsx-runtime";
function InputOTP({
  className,
  containerClassName,
  maxLength = 6,
  ...props
}) {
  return /* @__PURE__ */ jsx27(
    OTPInput,
    {
      "data-slot": "input-otp",
      maxLength,
      containerClassName: cn("flex items-center gap-2 has-disabled:opacity-50", containerClassName),
      className: cn("disabled:cursor-not-allowed", className),
      ...props
    }
  );
}
function InputOTPGroup({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx27(
    "div",
    {
      "data-slot": "input-otp-group",
      className: cn("flex items-center", className),
      ...props
    }
  );
}
function InputOTPSlot({
  index,
  className,
  ...props
}) {
  const inputOTPContext = React6.useContext(OTPInputContext);
  const { char, hasFakeCaret, isActive } = inputOTPContext?.slots[index] ?? {};
  return /* @__PURE__ */ jsxs14(
    "div",
    {
      "data-slot": "input-otp-slot",
      "data-active": isActive,
      className: cn(
        "data-[active=true]:border-ring data-[active=true]:ring-ring/50 data-[active=true]:aria-invalid:ring-destructive/20 dark:data-[active=true]:aria-invalid:ring-destructive/40 aria-invalid:border-destructive data-[active=true]:aria-invalid:border-destructive dark:bg-input/30 border-input relative flex h-9 w-9 items-center justify-center border-y border-r text-sm shadow-xs transition-all outline-none first:rounded-l-md first:border-l last:rounded-r-md data-[active=true]:z-10 data-[active=true]:ring-[3px]",
        className
      ),
      ...props,
      children: [
        char,
        hasFakeCaret && /* @__PURE__ */ jsx27(
          "div",
          {
            className: "pointer-events-none absolute inset-0 flex items-center justify-center",
            children: /* @__PURE__ */ jsx27("div", { className: "animate-caret-blink bg-foreground h-4 w-px duration-1000" })
          }
        )
      ]
    }
  );
}
function InputOTPSeparator({
  ...props
}) {
  return /* @__PURE__ */ jsx27("div", { "data-slot": "input-otp-separator", role: "separator", ...props, children: /* @__PURE__ */ jsx27(MinusIcon, {}) });
}

// src/components/ui/input.tsx
import { jsx as jsx28 } from "react/jsx-runtime";
function Input({
  className,
  type,
  ...props
}) {
  return /* @__PURE__ */ jsx28(
    "input",
    {
      type,
      "data-slot": "input",
      className: cn(
        "file:text-neutral-700 placeholder:text-neutral-400 selection:bg-primary-500 selection:text-white dark:bg-neutral-800 border-neutral-300 flex h-10 w-full min-w-0 rounded-lg border bg-white px-3 py-2 text-sm shadow-sm transition-all outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-neutral-100 font-['Public_Sans',sans-serif] dark:border-neutral-600 dark:text-neutral-100 dark:placeholder:text-neutral-500",
        "focus-visible:border-primary-500 focus-visible:ring-primary-500/20 focus-visible:ring-[3px]",
        "aria-invalid:ring-error-500/20 dark:aria-invalid:ring-error-500/40 aria-invalid:border-error-500",
        className
      ),
      ...props
    }
  );
}

// src/components/ui/menubar.tsx
import * as MenubarPrimitive from "@radix-ui/react-menubar";
import { CheckIcon as CheckIcon4, ChevronRightIcon as ChevronRightIcon3, CircleIcon as CircleIcon3 } from "lucide-react";
import { jsx as jsx29, jsxs as jsxs15 } from "react/jsx-runtime";
function Menubar({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx29(
    MenubarPrimitive.Root,
    {
      "data-slot": "menubar",
      className: cn(
        "bg-background flex h-9 items-center gap-1 rounded-md border p-1 shadow-xs",
        className
      ),
      ...props
    }
  );
}
function MenubarMenu({
  ...props
}) {
  return /* @__PURE__ */ jsx29(MenubarPrimitive.Menu, { "data-slot": "menubar-menu", ...props });
}
function MenubarGroup({
  ...props
}) {
  return /* @__PURE__ */ jsx29(MenubarPrimitive.Group, { "data-slot": "menubar-group", ...props });
}
function MenubarPortal({
  ...props
}) {
  return /* @__PURE__ */ jsx29(MenubarPrimitive.Portal, { "data-slot": "menubar-portal", ...props });
}
function MenubarRadioGroup({
  ...props
}) {
  return /* @__PURE__ */ jsx29(MenubarPrimitive.RadioGroup, { "data-slot": "menubar-radio-group", ...props });
}
function MenubarTrigger({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx29(
    MenubarPrimitive.Trigger,
    {
      "data-slot": "menubar-trigger",
      className: cn(
        "focus:bg-accent focus:text-accent-foreground data-[state=open]:bg-accent data-[state=open]:text-accent-foreground flex items-center rounded-sm px-2 py-1 text-sm font-medium outline-hidden select-none",
        className
      ),
      ...props
    }
  );
}
function MenubarContent({
  className,
  align = "start",
  alignOffset = -4,
  sideOffset = 8,
  ...props
}) {
  return /* @__PURE__ */ jsx29(MenubarPortal, { children: /* @__PURE__ */ jsx29(
    MenubarPrimitive.Content,
    {
      "data-slot": "menubar-content",
      align,
      alignOffset,
      sideOffset,
      className: cn(
        "bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 min-w-[12rem] origin-(--radix-menubar-content-transform-origin) overflow-hidden rounded-md border p-1 shadow-md",
        className
      ),
      ...props
    }
  ) });
}
function MenubarItem({
  className,
  inset,
  variant = "default",
  ...props
}) {
  return /* @__PURE__ */ jsx29(
    MenubarPrimitive.Item,
    {
      "data-slot": "menubar-item",
      "data-inset": inset,
      "data-variant": variant,
      className: cn(
        "focus:bg-accent focus:text-accent-foreground data-[variant=destructive]:text-destructive data-[variant=destructive]:focus:bg-destructive/10 dark:data-[variant=destructive]:focus:bg-destructive/20 data-[variant=destructive]:focus:text-destructive data-[variant=destructive]:*:[svg]:!text-destructive [&_svg:not([class*='text-'])]:text-muted-foreground relative flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[inset]:pl-8 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      ),
      ...props
    }
  );
}
function MenubarCheckboxItem({
  className,
  children,
  checked,
  ...props
}) {
  return /* @__PURE__ */ jsxs15(
    MenubarPrimitive.CheckboxItem,
    {
      "data-slot": "menubar-checkbox-item",
      className: cn(
        "focus:bg-accent focus:text-accent-foreground relative flex cursor-default items-center gap-2 rounded-xs py-1.5 pr-2 pl-8 text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      ),
      checked,
      ...props,
      children: [
        /* @__PURE__ */ jsx29(
          "span",
          {
            className: "pointer-events-none absolute left-2 flex size-3.5 items-center justify-center",
            children: /* @__PURE__ */ jsx29(MenubarPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx29(CheckIcon4, { className: "size-4" }) })
          }
        ),
        children
      ]
    }
  );
}
function MenubarRadioItem({
  className,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsxs15(
    MenubarPrimitive.RadioItem,
    {
      "data-slot": "menubar-radio-item",
      className: cn(
        "focus:bg-accent focus:text-accent-foreground relative flex cursor-default items-center gap-2 rounded-xs py-1.5 pr-2 pl-8 text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      ),
      ...props,
      children: [
        /* @__PURE__ */ jsx29(
          "span",
          {
            className: "pointer-events-none absolute left-2 flex size-3.5 items-center justify-center",
            children: /* @__PURE__ */ jsx29(MenubarPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx29(CircleIcon3, { className: "size-2 fill-current" }) })
          }
        ),
        children
      ]
    }
  );
}
function MenubarLabel({
  className,
  inset,
  ...props
}) {
  return /* @__PURE__ */ jsx29(
    MenubarPrimitive.Label,
    {
      "data-slot": "menubar-label",
      "data-inset": inset,
      className: cn("px-2 py-1.5 text-sm font-medium data-[inset]:pl-8", className),
      ...props
    }
  );
}
function MenubarSeparator({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx29(
    MenubarPrimitive.Separator,
    {
      "data-slot": "menubar-separator",
      className: cn("bg-border -mx-1 my-1 h-px", className),
      ...props
    }
  );
}
function MenubarShortcut({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx29(
    "span",
    {
      "data-slot": "menubar-shortcut",
      className: cn("text-muted-foreground ml-auto text-xs tracking-widest", className),
      ...props
    }
  );
}
function MenubarSub({
  ...props
}) {
  return /* @__PURE__ */ jsx29(MenubarPrimitive.Sub, { "data-slot": "menubar-sub", ...props });
}
function MenubarSubTrigger({
  className,
  inset,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsxs15(
    MenubarPrimitive.SubTrigger,
    {
      "data-slot": "menubar-sub-trigger",
      "data-inset": inset,
      className: cn(
        "focus:bg-accent focus:text-accent-foreground data-[state=open]:bg-accent data-[state=open]:text-accent-foreground flex cursor-default items-center rounded-sm px-2 py-1.5 text-sm outline-none select-none data-[inset]:pl-8",
        className
      ),
      ...props,
      children: [
        children,
        /* @__PURE__ */ jsx29(ChevronRightIcon3, { className: "ml-auto h-4 w-4" })
      ]
    }
  );
}
function MenubarSubContent({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx29(
    MenubarPrimitive.SubContent,
    {
      "data-slot": "menubar-sub-content",
      className: cn(
        "bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 min-w-[8rem] origin-(--radix-menubar-content-transform-origin) overflow-hidden rounded-md border p-1 shadow-lg",
        className
      ),
      ...props
    }
  );
}

// src/components/ui/navigation-menu.tsx
import * as NavigationMenuPrimitive from "@radix-ui/react-navigation-menu";
import { cva as cva5 } from "class-variance-authority";
import { ChevronDownIcon as ChevronDownIcon2 } from "lucide-react";
import { jsx as jsx30, jsxs as jsxs16 } from "react/jsx-runtime";
function NavigationMenu({
  className,
  children,
  viewport = true,
  ...props
}) {
  return /* @__PURE__ */ jsxs16(
    NavigationMenuPrimitive.Root,
    {
      "data-slot": "navigation-menu",
      "data-viewport": viewport,
      className: cn(
        "group/navigation-menu relative flex max-w-max flex-1 items-center justify-center",
        className
      ),
      ...props,
      children: [
        children,
        viewport && /* @__PURE__ */ jsx30(NavigationMenuViewport, {})
      ]
    }
  );
}
function NavigationMenuList({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx30(
    NavigationMenuPrimitive.List,
    {
      "data-slot": "navigation-menu-list",
      className: cn("group flex flex-1 list-none items-center justify-center gap-1", className),
      ...props
    }
  );
}
function NavigationMenuItem({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx30(
    NavigationMenuPrimitive.Item,
    {
      "data-slot": "navigation-menu-item",
      className: cn("relative", className),
      ...props
    }
  );
}
var navigationMenuTriggerStyle = cva5(
  "group inline-flex h-9 w-max items-center justify-center rounded-md bg-background px-4 py-2 text-sm font-medium hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground disabled:pointer-events-none disabled:opacity-50 data-[state=open]:hover:bg-accent data-[state=open]:text-accent-foreground data-[state=open]:focus:bg-accent data-[state=open]:bg-accent/50 focus-visible:ring-ring/50 outline-none transition-[color,box-shadow] focus-visible:ring-[3px] focus-visible:outline-1"
);
function NavigationMenuTrigger({
  className,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsxs16(
    NavigationMenuPrimitive.Trigger,
    {
      "data-slot": "navigation-menu-trigger",
      className: cn(navigationMenuTriggerStyle(), "group", className),
      ...props,
      children: [
        children,
        " ",
        /* @__PURE__ */ jsx30(
          ChevronDownIcon2,
          {
            className: "relative top-[1px] ml-1 size-3 transition duration-300 group-data-[state=open]:rotate-180",
            "aria-hidden": "true"
          }
        )
      ]
    }
  );
}
function NavigationMenuContent({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx30(
    NavigationMenuPrimitive.Content,
    {
      "data-slot": "navigation-menu-content",
      className: cn(
        "data-[motion^=from-]:animate-in data-[motion^=to-]:animate-out data-[motion^=from-]:fade-in data-[motion^=to-]:fade-out data-[motion=from-end]:slide-in-from-right-52 data-[motion=from-start]:slide-in-from-left-52 data-[motion=to-end]:slide-out-to-right-52 data-[motion=to-start]:slide-out-to-left-52 top-0 left-0 w-full p-2 pr-2.5 md:absolute md:w-auto",
        "group-data-[viewport=false]/navigation-menu:bg-popover group-data-[viewport=false]/navigation-menu:text-popover-foreground group-data-[viewport=false]/navigation-menu:data-[state=open]:animate-in group-data-[viewport=false]/navigation-menu:data-[state=closed]:animate-out group-data-[viewport=false]/navigation-menu:data-[state=closed]:zoom-out-95 group-data-[viewport=false]/navigation-menu:data-[state=open]:zoom-in-95 group-data-[viewport=false]/navigation-menu:data-[state=open]:fade-in-0 group-data-[viewport=false]/navigation-menu:data-[state=closed]:fade-out-0 group-data-[viewport=false]/navigation-menu:top-full group-data-[viewport=false]/navigation-menu:mt-1.5 group-data-[viewport=false]/navigation-menu:overflow-hidden group-data-[viewport=false]/navigation-menu:rounded-md group-data-[viewport=false]/navigation-menu:border group-data-[viewport=false]/navigation-menu:shadow group-data-[viewport=false]/navigation-menu:duration-200 **:data-[slot=navigation-menu-link]:focus:ring-0 **:data-[slot=navigation-menu-link]:focus:outline-none",
        className
      ),
      ...props
    }
  );
}
function NavigationMenuViewport({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx30(
    "div",
    {
      className: cn("absolute top-full left-0 isolate z-50 flex justify-center"),
      children: /* @__PURE__ */ jsx30(
        NavigationMenuPrimitive.Viewport,
        {
          "data-slot": "navigation-menu-viewport",
          className: cn(
            "origin-top-center bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-90 relative mt-1.5 h-[var(--radix-navigation-menu-viewport-height)] w-full overflow-hidden rounded-md border shadow md:w-[var(--radix-navigation-menu-viewport-width)]",
            className
          ),
          ...props
        }
      )
    }
  );
}
function NavigationMenuLink({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx30(
    NavigationMenuPrimitive.Link,
    {
      "data-slot": "navigation-menu-link",
      className: cn(
        "data-[active=true]:focus:bg-accent data-[active=true]:hover:bg-accent data-[active=true]:bg-accent/50 data-[active=true]:text-accent-foreground hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus-visible:ring-ring/50 [&_svg:not([class*='text-'])]:text-muted-foreground flex flex-col gap-1 rounded-sm p-2 text-sm transition-all outline-none focus-visible:ring-[3px] focus-visible:outline-1 [&_svg:not([class*='size-'])]:size-4",
        className
      ),
      ...props
    }
  );
}
function NavigationMenuIndicator({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx30(
    NavigationMenuPrimitive.Indicator,
    {
      "data-slot": "navigation-menu-indicator",
      className: cn(
        "data-[state=visible]:animate-in data-[state=hidden]:animate-out data-[state=hidden]:fade-out data-[state=visible]:fade-in top-full z-[1] flex h-1.5 items-end justify-center overflow-hidden",
        className
      ),
      ...props,
      children: /* @__PURE__ */ jsx30(
        "div",
        {
          className: "bg-border relative top-[60%] h-2 w-2 rotate-45 rounded-tl-sm shadow-md"
        }
      )
    }
  );
}

// src/components/ui/page-scaffold.tsx
import { jsx as jsx31, jsxs as jsxs17 } from "react/jsx-runtime";
function PageScaffold({
  title,
  subtitle,
  titleIcon,
  actions,
  banner,
  kpis,
  children,
  className,
  headerClassName,
  kpiClassName,
  bodyClassName
}) {
  return /* @__PURE__ */ jsxs17("div", { className: cn("space-y-8", className), children: [
    /* @__PURE__ */ jsxs17(
      "header",
      {
        className: cn(
          "flex items-start justify-between gap-4",
          headerClassName
        ),
        children: [
          /* @__PURE__ */ jsxs17("div", { className: "flex flex-col", children: [
            /* @__PURE__ */ jsxs17("h1", { className: "flex items-center gap-2 text-xl font-semibold text-[var(--text-primary)]", children: [
              titleIcon && /* @__PURE__ */ jsx31("span", { className: "flex-shrink-0 text-[var(--text-secondary)]", children: titleIcon }),
              title
            ] }),
            subtitle && /* @__PURE__ */ jsx31("p", { className: "mt-1 text-sm text-[var(--text-secondary)]", children: subtitle })
          ] }),
          actions && /* @__PURE__ */ jsx31("div", { className: "flex items-center gap-2 flex-shrink-0", children: actions })
        ]
      }
    ),
    banner && /* @__PURE__ */ jsx31("div", { children: banner }),
    kpis && /* @__PURE__ */ jsx31(
      "section",
      {
        "aria-label": "Key metrics",
        className: cn(
          "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5",
          kpiClassName
        ),
        children: kpis
      }
    ),
    /* @__PURE__ */ jsx31("div", { className: cn("space-y-8", bodyClassName), children })
  ] });
}
PageScaffold.displayName = "PageScaffold";

// src/components/ui/pagination.tsx
import {
  ChevronLeftIcon,
  ChevronRightIcon as ChevronRightIcon4,
  MoreHorizontalIcon
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { jsx as jsx32, jsxs as jsxs18 } from "react/jsx-runtime";
function Pagination({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx32(
    "nav",
    {
      role: "navigation",
      "aria-label": "pagination",
      "data-slot": "pagination",
      className: cn("mx-auto flex w-full justify-center", className),
      ...props
    }
  );
}
function PaginationContent({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx32(
    "ul",
    {
      "data-slot": "pagination-content",
      className: cn("flex flex-row items-center gap-1", className),
      ...props
    }
  );
}
function PaginationItem({
  ...props
}) {
  return /* @__PURE__ */ jsx32("li", { "data-slot": "pagination-item", ...props });
}
function PaginationLink({
  className,
  isActive,
  size = "icon",
  ...props
}) {
  const { t } = useTranslation();
  return /* @__PURE__ */ jsx32(
    "a",
    {
      "aria-current": isActive ? "page" : void 0,
      "data-slot": "pagination-link",
      "data-active": isActive,
      className: cn(buttonVariants({
        variant: isActive ? "outline" : "ghost",
        size
      }), className),
      "aria-label": isActive ? t("feedback.currentPage") : t("feedback.goToPage"),
      ...props
    }
  );
}
function PaginationPrevious({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsxs18(
    PaginationLink,
    {
      "aria-label": "Go to previous page",
      size: "default",
      className: cn("gap-1 px-2.5 sm:pl-2.5", className),
      ...props,
      children: [
        /* @__PURE__ */ jsx32(ChevronLeftIcon, {}),
        /* @__PURE__ */ jsx32("span", { className: "hidden sm:block", children: "Previous" })
      ]
    }
  );
}
function PaginationNext({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsxs18(
    PaginationLink,
    {
      "aria-label": "Go to next page",
      size: "default",
      className: cn("gap-1 px-2.5 sm:pr-2.5", className),
      ...props,
      children: [
        /* @__PURE__ */ jsx32("span", { className: "hidden sm:block", children: "Next" }),
        /* @__PURE__ */ jsx32(ChevronRightIcon4, {})
      ]
    }
  );
}
function PaginationEllipsis({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsxs18(
    "span",
    {
      "aria-hidden": true,
      "data-slot": "pagination-ellipsis",
      className: cn("flex size-9 items-center justify-center", className),
      ...props,
      children: [
        /* @__PURE__ */ jsx32(MoreHorizontalIcon, { className: "size-4" }),
        /* @__PURE__ */ jsx32("span", { className: "sr-only", children: "More pages" })
      ]
    }
  );
}

// src/components/ui/popover.tsx
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { jsx as jsx33 } from "react/jsx-runtime";
function Popover({
  ...props
}) {
  return /* @__PURE__ */ jsx33(PopoverPrimitive.Root, { "data-slot": "popover", ...props });
}
function PopoverTrigger({
  ...props
}) {
  return /* @__PURE__ */ jsx33(PopoverPrimitive.Trigger, { "data-slot": "popover-trigger", ...props });
}
function PopoverContent({
  className,
  align = "center",
  sideOffset = 4,
  ...props
}) {
  return /* @__PURE__ */ jsx33(PopoverPrimitive.Portal, { children: /* @__PURE__ */ jsx33(
    PopoverPrimitive.Content,
    {
      "data-slot": "popover-content",
      align,
      sideOffset,
      className: cn(
        "bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 w-72 origin-(--radix-popover-content-transform-origin) rounded-md border p-4 shadow-md outline-hidden",
        className
      ),
      ...props
    }
  ) });
}
function PopoverAnchor({
  ...props
}) {
  return /* @__PURE__ */ jsx33(PopoverPrimitive.Anchor, { "data-slot": "popover-anchor", ...props });
}

// src/components/ui/progress.tsx
import * as ProgressPrimitive from "@radix-ui/react-progress";
import { cva as cva6 } from "class-variance-authority";
import { jsx as jsx34, jsxs as jsxs19 } from "react/jsx-runtime";
var progressVariants = cva6(
  "relative h-2 w-full overflow-hidden rounded-full",
  {
    variants: {
      variant: {
        default: "bg-primary-100",
        success: "bg-success-100",
        warning: "bg-warning-100",
        error: "bg-error-100",
        pink: "bg-pink-100",
        cyan: "bg-cyan-100"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);
var indicatorVariants = cva6(
  "h-full w-full flex-1 transition-all rounded-full",
  {
    variants: {
      variant: {
        default: "bg-primary-500",
        success: "bg-success-500",
        warning: "bg-warning-500",
        error: "bg-error-500",
        pink: "bg-pink-500",
        cyan: "bg-cyan-500"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);
function Progress({
  className,
  value,
  variant,
  showLabel = false,
  ...props
}) {
  return /* @__PURE__ */ jsxs19("div", { className: "flex items-center gap-3 w-full", children: [
    /* @__PURE__ */ jsx34(
      ProgressPrimitive.Root,
      {
        "data-slot": "progress",
        className: cn(progressVariants({ variant }), className),
        ...props,
        children: /* @__PURE__ */ jsx34(
          ProgressPrimitive.Indicator,
          {
            "data-slot": "progress-indicator",
            className: cn(indicatorVariants({ variant })),
            style: { transform: `translateX(-${100 - (value || 0)}%)` }
          }
        )
      }
    ),
    showLabel && /* @__PURE__ */ jsxs19("span", { className: "text-sm font-medium text-neutral-700 dark:text-neutral-300 min-w-[3ch] text-right", children: [
      value || 0,
      "%"
    ] })
  ] });
}

// src/components/ui/radio-group.tsx
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { CircleIcon as CircleIcon4 } from "lucide-react";
import { jsx as jsx35 } from "react/jsx-runtime";
function RadioGroup4({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx35(
    RadioGroupPrimitive.Root,
    {
      "data-slot": "radio-group",
      className: cn("grid gap-3", className),
      ...props
    }
  );
}
function RadioGroupItem({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx35(
    RadioGroupPrimitive.Item,
    {
      "data-slot": "radio-group-item",
      className: cn(
        "border-input text-primary focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 aspect-square size-4 shrink-0 rounded-full border shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50",
        className
      ),
      ...props,
      children: /* @__PURE__ */ jsx35(
        RadioGroupPrimitive.Indicator,
        {
          "data-slot": "radio-group-indicator",
          className: "relative flex items-center justify-center",
          children: /* @__PURE__ */ jsx35(
            CircleIcon4,
            {
              className: "fill-primary absolute top-1/2 left-1/2 size-2 -translate-x-1/2 -translate-y-1/2"
            }
          )
        }
      )
    }
  );
}

// src/components/ui/resizable.tsx
import { GripVerticalIcon } from "lucide-react";
import * as ResizablePrimitive from "react-resizable-panels";
import { jsx as jsx36 } from "react/jsx-runtime";
function ResizablePanelGroup({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx36(
    ResizablePrimitive.PanelGroup,
    {
      "data-slot": "resizable-panel-group",
      className: cn(
        "flex h-full w-full data-[panel-group-direction=vertical]:flex-col",
        className
      ),
      ...props
    }
  );
}
function ResizablePanel({
  ...props
}) {
  return /* @__PURE__ */ jsx36(ResizablePrimitive.Panel, { "data-slot": "resizable-panel", ...props });
}
function ResizableHandle({
  withHandle,
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx36(
    ResizablePrimitive.PanelResizeHandle,
    {
      "data-slot": "resizable-handle",
      className: cn(
        "bg-border focus-visible:ring-ring relative flex w-px items-center justify-center after:absolute after:inset-y-0 after:left-1/2 after:w-1 after:-translate-x-1/2 focus-visible:ring-1 focus-visible:ring-offset-1 focus-visible:outline-hidden data-[panel-group-direction=vertical]:h-px data-[panel-group-direction=vertical]:w-full data-[panel-group-direction=vertical]:after:left-0 data-[panel-group-direction=vertical]:after:h-1 data-[panel-group-direction=vertical]:after:w-full data-[panel-group-direction=vertical]:after:-translate-y-1/2 data-[panel-group-direction=vertical]:after:translate-x-0 [&[data-panel-group-direction=vertical]>div]:rotate-90",
        className
      ),
      ...props,
      children: withHandle && /* @__PURE__ */ jsx36(
        "div",
        {
          className: "bg-border z-10 flex h-4 w-3 items-center justify-center rounded-xs border",
          children: /* @__PURE__ */ jsx36(GripVerticalIcon, { className: "size-2.5" })
        }
      )
    }
  );
}

// src/components/ui/scroll-area.tsx
import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area";
import { jsx as jsx37, jsxs as jsxs20 } from "react/jsx-runtime";
function ScrollArea({
  className,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsxs20(ScrollAreaPrimitive.Root, { "data-slot": "scroll-area", className: cn("relative", className), ...props, children: [
    /* @__PURE__ */ jsx37(
      ScrollAreaPrimitive.Viewport,
      {
        "data-slot": "scroll-area-viewport",
        className: "focus-visible:ring-ring/50 size-full rounded-[inherit] transition-[color,box-shadow] outline-none focus-visible:ring-[3px] focus-visible:outline-1",
        children
      }
    ),
    /* @__PURE__ */ jsx37(ScrollBar, {}),
    /* @__PURE__ */ jsx37(ScrollAreaPrimitive.Corner, {})
  ] });
}
function ScrollBar({
  className,
  orientation = "vertical",
  ...props
}) {
  return /* @__PURE__ */ jsx37(
    ScrollAreaPrimitive.ScrollAreaScrollbar,
    {
      "data-slot": "scroll-area-scrollbar",
      orientation,
      className: cn(
        "flex touch-none p-px transition-colors select-none",
        orientation === "vertical" && "h-full w-2.5 border-l border-l-transparent",
        orientation === "horizontal" && "h-2.5 flex-col border-t border-t-transparent",
        className
      ),
      ...props,
      children: /* @__PURE__ */ jsx37(
        ScrollAreaPrimitive.ScrollAreaThumb,
        {
          "data-slot": "scroll-area-thumb",
          className: "bg-border relative flex-1 rounded-full"
        }
      )
    }
  );
}

// src/components/dashboard/section-card.tsx
import { jsx as jsx38, jsxs as jsxs21 } from "react/jsx-runtime";
function SectionCard({
  title,
  subtitle,
  icon,
  action,
  children,
  className,
  ...props
}) {
  return /* @__PURE__ */ jsxs21(
    "div",
    {
      className: cn(
        "rounded-xl border border-[var(--border)] bg-[var(--surface)] shadow-card",
        className
      ),
      ...props,
      children: [
        /* @__PURE__ */ jsxs21("div", { className: "flex items-center justify-between border-b border-[var(--border)] px-5 py-4", children: [
          /* @__PURE__ */ jsxs21("div", { className: "flex items-center gap-2", children: [
            icon && /* @__PURE__ */ jsx38("span", { className: "text-[var(--text-secondary)]", children: icon }),
            /* @__PURE__ */ jsxs21("div", { children: [
              /* @__PURE__ */ jsx38("h2", { className: "text-sm font-semibold text-[var(--text-primary)]", children: title }),
              subtitle && /* @__PURE__ */ jsx38("p", { className: "mt-0.5 text-xs text-[var(--text-secondary)]", children: subtitle })
            ] })
          ] }),
          action && /* @__PURE__ */ jsx38("div", { className: "text-xs", children: action })
        ] }),
        /* @__PURE__ */ jsx38("div", { className: "px-5 py-4", children })
      ]
    }
  );
}

// src/components/ui/section-template.tsx
import { jsx as jsx39, jsxs as jsxs22 } from "react/jsx-runtime";
function SectionTemplate({
  title,
  description,
  actions,
  children,
  variant = "plain",
  className,
  headerClassName,
  bodyClassName
}) {
  if (variant === "card") {
    if (process.env.NODE_ENV !== "production" && description && typeof description !== "string") {
      console.warn('SectionTemplate: The "description" prop must be a string when using the "card" variant. The provided ReactNode will not be rendered.');
    }
    const subtitle = typeof description === "string" ? description : void 0;
    return /* @__PURE__ */ jsx39("section", { className, children: /* @__PURE__ */ jsx39(
      SectionCard,
      {
        title,
        subtitle,
        action: actions,
        className: bodyClassName,
        children
      }
    ) });
  }
  return /* @__PURE__ */ jsxs22("section", { className, children: [
    /* @__PURE__ */ jsxs22(
      "div",
      {
        className: cn(
          "flex items-start justify-between gap-4",
          headerClassName
        ),
        children: [
          /* @__PURE__ */ jsxs22("div", { children: [
            /* @__PURE__ */ jsx39("h2", { className: "text-base font-semibold text-[var(--text-primary)]", children: title }),
            description && /* @__PURE__ */ jsx39("p", { className: "mt-1 text-sm text-[var(--text-secondary)]", children: description })
          ] }),
          actions && /* @__PURE__ */ jsx39("div", { className: "flex items-center gap-2 flex-shrink-0", children: actions })
        ]
      }
    ),
    /* @__PURE__ */ jsx39("div", { className: cn("mt-4 space-y-4", bodyClassName), children })
  ] });
}
SectionTemplate.displayName = "SectionTemplate";

// src/components/ui/select.tsx
import * as SelectPrimitive from "@radix-ui/react-select";
import { CheckIcon as CheckIcon5, ChevronDownIcon as ChevronDownIcon3, ChevronUpIcon } from "lucide-react";
import { jsx as jsx40, jsxs as jsxs23 } from "react/jsx-runtime";
function Select({
  ...props
}) {
  return /* @__PURE__ */ jsx40(SelectPrimitive.Root, { "data-slot": "select", ...props });
}
function SelectGroup({
  ...props
}) {
  return /* @__PURE__ */ jsx40(SelectPrimitive.Group, { "data-slot": "select-group", ...props });
}
function SelectValue({
  ...props
}) {
  return /* @__PURE__ */ jsx40(SelectPrimitive.Value, { "data-slot": "select-value", ...props });
}
function SelectTrigger({
  className,
  size = "default",
  children,
  ...props
}) {
  return /* @__PURE__ */ jsxs23(
    SelectPrimitive.Trigger,
    {
      "data-slot": "select-trigger",
      "data-size": size,
      className: cn(
        "border-input data-[placeholder]:text-muted-foreground [&_svg:not([class*='text-'])]:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 dark:hover:bg-input/50 flex w-fit items-center justify-between gap-2 rounded-md border bg-transparent px-3 py-2 text-sm whitespace-nowrap shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 data-[size=default]:h-9 data-[size=sm]:h-8 *:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-2 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      ),
      ...props,
      children: [
        children,
        /* @__PURE__ */ jsx40(SelectPrimitive.Icon, { asChild: true, children: /* @__PURE__ */ jsx40(ChevronDownIcon3, { className: "size-4 opacity-50" }) })
      ]
    }
  );
}
function SelectContent({
  className,
  children,
  position = "popper",
  ...props
}) {
  return /* @__PURE__ */ jsx40(SelectPrimitive.Portal, { children: /* @__PURE__ */ jsxs23(
    SelectPrimitive.Content,
    {
      "data-slot": "select-content",
      className: cn(
        "bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-50 max-h-(--radix-select-content-available-height) min-w-[8rem] origin-(--radix-select-content-transform-origin) overflow-x-hidden overflow-y-auto rounded-md border shadow-md",
        position === "popper" && "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
        className
      ),
      position,
      ...props,
      children: [
        /* @__PURE__ */ jsx40(SelectScrollUpButton, {}),
        /* @__PURE__ */ jsx40(
          SelectPrimitive.Viewport,
          {
            className: cn("p-1", position === "popper" && "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)] scroll-my-1"),
            children
          }
        ),
        /* @__PURE__ */ jsx40(SelectScrollDownButton, {})
      ]
    }
  ) });
}
function SelectLabel({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx40(
    SelectPrimitive.Label,
    {
      "data-slot": "select-label",
      className: cn("text-muted-foreground px-2 py-1.5 text-xs", className),
      ...props
    }
  );
}
function SelectItem({
  className,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsxs23(
    SelectPrimitive.Item,
    {
      "data-slot": "select-item",
      className: cn(
        "focus:bg-accent focus:text-accent-foreground [&_svg:not([class*='text-'])]:text-muted-foreground relative flex w-full cursor-default items-center gap-2 rounded-sm py-1.5 pr-8 pl-2 text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2",
        className
      ),
      ...props,
      children: [
        /* @__PURE__ */ jsx40("span", { className: "absolute right-2 flex size-3.5 items-center justify-center", children: /* @__PURE__ */ jsx40(SelectPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx40(CheckIcon5, { className: "size-4" }) }) }),
        /* @__PURE__ */ jsx40(SelectPrimitive.ItemText, { children })
      ]
    }
  );
}
function SelectSeparator({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx40(
    SelectPrimitive.Separator,
    {
      "data-slot": "select-separator",
      className: cn("bg-border pointer-events-none -mx-1 my-1 h-px", className),
      ...props
    }
  );
}
function SelectScrollUpButton({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx40(
    SelectPrimitive.ScrollUpButton,
    {
      "data-slot": "select-scroll-up-button",
      className: cn("flex cursor-default items-center justify-center py-1", className),
      ...props,
      children: /* @__PURE__ */ jsx40(ChevronUpIcon, { className: "size-4" })
    }
  );
}
function SelectScrollDownButton({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx40(
    SelectPrimitive.ScrollDownButton,
    {
      "data-slot": "select-scroll-down-button",
      className: cn("flex cursor-default items-center justify-center py-1", className),
      ...props,
      children: /* @__PURE__ */ jsx40(ChevronDownIcon3, { className: "size-4" })
    }
  );
}

// src/components/ui/separator.tsx
import * as SeparatorPrimitive from "@radix-ui/react-separator";
import { jsx as jsx41 } from "react/jsx-runtime";
function Separator5({
  className,
  orientation = "horizontal",
  decorative = true,
  ...props
}) {
  return /* @__PURE__ */ jsx41(
    SeparatorPrimitive.Root,
    {
      "data-slot": "separator",
      decorative,
      orientation,
      className: cn(
        "bg-border shrink-0",
        orientation === "horizontal" ? "h-px w-full" : "h-full w-px",
        className
      ),
      ...props
    }
  );
}

// src/components/ui/sheet.tsx
import * as SheetPrimitive from "@radix-ui/react-dialog";
import { XIcon as XIcon2 } from "lucide-react";
import { jsx as jsx42, jsxs as jsxs24 } from "react/jsx-runtime";
function Sheet({
  ...props
}) {
  return /* @__PURE__ */ jsx42(SheetPrimitive.Root, { "data-slot": "sheet", ...props });
}
function SheetTrigger({
  ...props
}) {
  return /* @__PURE__ */ jsx42(SheetPrimitive.Trigger, { "data-slot": "sheet-trigger", ...props });
}
function SheetClose({
  ...props
}) {
  return /* @__PURE__ */ jsx42(SheetPrimitive.Close, { "data-slot": "sheet-close", ...props });
}
function SheetPortal({
  ...props
}) {
  return /* @__PURE__ */ jsx42(SheetPrimitive.Portal, { "data-slot": "sheet-portal", ...props });
}
function SheetOverlay({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx42(
    SheetPrimitive.Overlay,
    {
      "data-slot": "sheet-overlay",
      className: cn(
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50",
        className
      ),
      ...props
    }
  );
}
function SheetContent({
  className,
  children,
  side = "right",
  ...props
}) {
  return /* @__PURE__ */ jsxs24(SheetPortal, { children: [
    /* @__PURE__ */ jsx42(SheetOverlay, {}),
    /* @__PURE__ */ jsxs24(
      SheetPrimitive.Content,
      {
        "data-slot": "sheet-content",
        className: cn(
          "bg-background data-[state=open]:animate-in data-[state=closed]:animate-out fixed z-50 flex flex-col gap-4 shadow-lg transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500",
          side === "right" && "data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right inset-y-0 right-0 h-full w-3/4 border-l sm:max-w-sm",
          side === "left" && "data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left inset-y-0 left-0 h-full w-3/4 border-r sm:max-w-sm",
          side === "top" && "data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top inset-x-0 top-0 h-auto border-b",
          side === "bottom" && "data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom inset-x-0 bottom-0 h-auto border-t",
          className
        ),
        ...props,
        children: [
          children,
          /* @__PURE__ */ jsxs24(
            SheetPrimitive.Close,
            {
              className: "ring-offset-background focus:ring-ring data-[state=open]:bg-secondary absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none",
              children: [
                /* @__PURE__ */ jsx42(XIcon2, { className: "size-4" }),
                /* @__PURE__ */ jsx42("span", { className: "sr-only", children: "Close" })
              ]
            }
          )
        ]
      }
    )
  ] });
}
function SheetHeader({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx42(
    "div",
    {
      "data-slot": "sheet-header",
      className: cn("flex flex-col gap-1.5 p-4", className),
      ...props
    }
  );
}
function SheetFooter({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx42(
    "div",
    {
      "data-slot": "sheet-footer",
      className: cn("mt-auto flex flex-col gap-2 p-4", className),
      ...props
    }
  );
}
function SheetTitle({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx42(
    SheetPrimitive.Title,
    {
      "data-slot": "sheet-title",
      className: cn("text-foreground font-semibold", className),
      ...props
    }
  );
}
function SheetDescription({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx42(
    SheetPrimitive.Description,
    {
      "data-slot": "sheet-description",
      className: cn("text-muted-foreground text-sm", className),
      ...props
    }
  );
}

// src/components/ui/sidebar.tsx
import * as React9 from "react";
import { Slot as Slot6 } from "@radix-ui/react-slot";
import { PanelLeftIcon } from "lucide-react";

// src/hooks/use-mobile.ts
import * as React7 from "react";
var MOBILE_BREAKPOINT = 768;
function useIsMobile() {
  const [isMobile, setIsMobile] = React7.useState(void 0);
  React7.useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
    const onChange = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };
    mql.addEventListener("change", onChange);
    setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    return () => mql.removeEventListener("change", onChange);
  }, []);
  return !!isMobile;
}

// src/components/ui/skeleton.tsx
import { jsx as jsx43 } from "react/jsx-runtime";
function Skeleton({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx43(
    "div",
    {
      "data-slot": "skeleton",
      className: cn("bg-primary/10 animate-pulse rounded-md", className),
      ...props
    }
  );
}

// src/components/ui/tooltip.tsx
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { jsx as jsx44, jsxs as jsxs25 } from "react/jsx-runtime";
function TooltipProvider({
  delayDuration = 0,
  ...props
}) {
  return /* @__PURE__ */ jsx44(TooltipPrimitive.Provider, { "data-slot": "tooltip-provider", delayDuration, ...props });
}
function Tooltip2({
  ...props
}) {
  return /* @__PURE__ */ jsx44(TooltipProvider, { children: /* @__PURE__ */ jsx44(TooltipPrimitive.Root, { "data-slot": "tooltip", ...props }) });
}
function TooltipTrigger({
  ...props
}) {
  return /* @__PURE__ */ jsx44(TooltipPrimitive.Trigger, { "data-slot": "tooltip-trigger", ...props });
}
function TooltipContent({
  className,
  sideOffset = 0,
  arrowClassName,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsx44(TooltipPrimitive.Portal, { children: /* @__PURE__ */ jsxs25(
    TooltipPrimitive.Content,
    {
      "data-slot": "tooltip-content",
      sideOffset,
      className: cn(
        "bg-primary text-primary-foreground animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 w-fit origin-(--radix-tooltip-content-transform-origin) rounded-md px-3 py-1.5 text-xs text-balance",
        className
      ),
      ...props,
      children: [
        children,
        /* @__PURE__ */ jsx44(
          TooltipPrimitive.Arrow,
          {
            className: cn(
              "z-50 size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px]",
              arrowClassName || "bg-primary fill-primary"
            )
          }
        )
      ]
    }
  ) });
}

// src/components/ui/sidebar-variants.ts
import { cva as cva7 } from "class-variance-authority";
var sidebarMenuButtonVariants = cva7(
  "peer/menu-button flex w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm outline-hidden ring-sidebar-ring transition-[width,height,padding] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 group-has-data-[sidebar=menu-action]/menu-item:pr-8 aria-disabled:pointer-events-none aria-disabled:opacity-50 data-[active=true]:bg-sidebar-accent data-[active=true]:font-medium data-[active=true]:text-sidebar-accent-foreground data-[state=open]:hover:bg-sidebar-accent data-[state=open]:hover:text-sidebar-accent-foreground group-data-[collapsible=icon]:size-8! group-data-[collapsible=icon]:p-2! [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
        outline: "bg-background shadow-[0_0_0_1px_hsl(var(--sidebar-border))] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground hover:shadow-[0_0_0_1px_hsl(var(--sidebar-accent))]"
      },
      size: {
        default: "h-8 text-sm",
        sm: "h-7 text-xs",
        lg: "h-12 text-sm group-data-[collapsible=icon]:p-0!"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default"
    }
  }
);

// src/components/ui/use-sidebar.ts
import * as React8 from "react";
var SidebarContext = React8.createContext(null);
function useSidebar() {
  const context = React8.useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider.");
  }
  return context;
}

// src/components/ui/sidebar.tsx
import { jsx as jsx45, jsxs as jsxs26 } from "react/jsx-runtime";
var SIDEBAR_COOKIE_NAME = "sidebar_state";
var SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;
var SIDEBAR_WIDTH = "16rem";
var SIDEBAR_WIDTH_MOBILE = "18rem";
var SIDEBAR_WIDTH_ICON = "3rem";
var SIDEBAR_KEYBOARD_SHORTCUT = "b";
function SidebarProvider({
  defaultOpen = true,
  open: openProp,
  onOpenChange: setOpenProp,
  className,
  style,
  children,
  ...props
}) {
  const isMobile = useIsMobile();
  const [openMobile, setOpenMobile] = React9.useState(false);
  const [_open, _setOpen] = React9.useState(defaultOpen);
  const open = openProp ?? _open;
  const setOpen = React9.useCallback((value) => {
    const openState = typeof value === "function" ? value(open) : value;
    if (setOpenProp) {
      setOpenProp(openState);
    } else {
      _setOpen(openState);
    }
    document.cookie = `${SIDEBAR_COOKIE_NAME}=${openState}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`;
  }, [setOpenProp, open]);
  const toggleSidebar = React9.useCallback(() => {
    return isMobile ? setOpenMobile((open2) => !open2) : setOpen((open2) => !open2);
  }, [isMobile, setOpen, setOpenMobile]);
  React9.useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === SIDEBAR_KEYBOARD_SHORTCUT && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        toggleSidebar();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [toggleSidebar]);
  const state = open ? "expanded" : "collapsed";
  const contextValue = React9.useMemo(() => ({
    state,
    open,
    setOpen,
    isMobile,
    openMobile,
    setOpenMobile,
    toggleSidebar
  }), [state, open, setOpen, isMobile, openMobile, setOpenMobile, toggleSidebar]);
  return /* @__PURE__ */ jsx45(SidebarContext.Provider, { value: contextValue, children: /* @__PURE__ */ jsx45(TooltipProvider, { delayDuration: 0, children: /* @__PURE__ */ jsx45(
    "div",
    {
      "data-slot": "sidebar-wrapper",
      style: {
        "--sidebar-width": SIDEBAR_WIDTH,
        "--sidebar-width-icon": SIDEBAR_WIDTH_ICON,
        ...style
      },
      className: cn(
        "group/sidebar-wrapper has-data-[variant=inset]:bg-sidebar flex min-h-svh w-full",
        className
      ),
      ...props,
      children
    }
  ) }) });
}
function Sidebar({
  side = "left",
  variant = "sidebar",
  collapsible = "offcanvas",
  className,
  children,
  ...props
}) {
  const { isMobile, state, openMobile, setOpenMobile } = useSidebar();
  if (collapsible === "none") {
    return /* @__PURE__ */ jsx45(
      "div",
      {
        "data-slot": "sidebar",
        className: cn(
          "bg-sidebar text-sidebar-foreground flex h-full w-(--sidebar-width) flex-col",
          className
        ),
        ...props,
        children
      }
    );
  }
  if (isMobile) {
    return /* @__PURE__ */ jsx45(Sheet, { open: openMobile, onOpenChange: setOpenMobile, ...props, children: /* @__PURE__ */ jsxs26(
      SheetContent,
      {
        "data-sidebar": "sidebar",
        "data-slot": "sidebar",
        "data-mobile": "true",
        className: "bg-sidebar text-sidebar-foreground w-(--sidebar-width) p-0 [&>button]:hidden",
        style: {
          "--sidebar-width": SIDEBAR_WIDTH_MOBILE
        },
        side,
        children: [
          /* @__PURE__ */ jsxs26(SheetHeader, { className: "sr-only", children: [
            /* @__PURE__ */ jsx45(SheetTitle, { children: "Sidebar" }),
            /* @__PURE__ */ jsx45(SheetDescription, { children: "Displays the mobile sidebar." })
          ] }),
          /* @__PURE__ */ jsx45("div", { className: "flex h-full w-full flex-col", children })
        ]
      }
    ) });
  }
  return /* @__PURE__ */ jsxs26(
    "div",
    {
      className: "group peer text-sidebar-foreground hidden md:block",
      "data-state": state,
      "data-collapsible": state === "collapsed" ? collapsible : "",
      "data-variant": variant,
      "data-side": side,
      "data-slot": "sidebar",
      children: [
        /* @__PURE__ */ jsx45(
          "div",
          {
            "data-slot": "sidebar-gap",
            className: cn(
              "relative w-(--sidebar-width) bg-transparent transition-[width] duration-200 ease-linear",
              "group-data-[collapsible=offcanvas]:w-0",
              "group-data-[side=right]:rotate-180",
              variant === "floating" || variant === "inset" ? "group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4)))]" : "group-data-[collapsible=icon]:w-(--sidebar-width-icon)"
            )
          }
        ),
        /* @__PURE__ */ jsx45(
          "div",
          {
            "data-slot": "sidebar-container",
            className: cn(
              "fixed inset-y-0 z-10 hidden h-svh w-(--sidebar-width) transition-[left,right,width] duration-200 ease-linear md:flex",
              side === "left" ? "left-0 group-data-[collapsible=offcanvas]:left-[calc(var(--sidebar-width)*-1)]" : "right-0 group-data-[collapsible=offcanvas]:right-[calc(var(--sidebar-width)*-1)]",
              variant === "floating" || variant === "inset" ? "p-2 group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4))+2px)]" : "group-data-[collapsible=icon]:w-(--sidebar-width-icon) group-data-[side=left]:border-r group-data-[side=right]:border-l",
              className
            ),
            ...props,
            children: /* @__PURE__ */ jsx45(
              "div",
              {
                "data-sidebar": "sidebar",
                "data-slot": "sidebar-inner",
                className: "bg-sidebar group-data-[variant=floating]:border-sidebar-border flex h-full w-full flex-col group-data-[variant=floating]:rounded-lg group-data-[variant=floating]:border group-data-[variant=floating]:shadow-sm",
                children
              }
            )
          }
        )
      ]
    }
  );
}
function SidebarTrigger({
  className,
  onClick,
  ...props
}) {
  const { toggleSidebar } = useSidebar();
  return /* @__PURE__ */ jsxs26(
    Button,
    {
      "data-sidebar": "trigger",
      "data-slot": "sidebar-trigger",
      variant: "ghost",
      size: "icon",
      className: cn("size-7", className),
      onClick: (event) => {
        onClick?.(event);
        toggleSidebar();
      },
      ...props,
      children: [
        /* @__PURE__ */ jsx45(PanelLeftIcon, {}),
        /* @__PURE__ */ jsx45("span", { className: "sr-only", children: "Toggle Sidebar" })
      ]
    }
  );
}
function SidebarRail({
  className,
  ...props
}) {
  const { toggleSidebar } = useSidebar();
  return /* @__PURE__ */ jsx45(
    "button",
    {
      "data-sidebar": "rail",
      "data-slot": "sidebar-rail",
      "aria-label": "Toggle Sidebar",
      tabIndex: -1,
      onClick: toggleSidebar,
      title: "Toggle Sidebar",
      className: cn(
        "hover:after:bg-sidebar-border absolute inset-y-0 z-20 hidden w-4 -translate-x-1/2 transition-all ease-linear group-data-[side=left]:-right-4 group-data-[side=right]:left-0 after:absolute after:inset-y-0 after:left-1/2 after:w-[2px] sm:flex",
        "in-data-[side=left]:cursor-w-resize in-data-[side=right]:cursor-e-resize",
        "[[data-side=left][data-state=collapsed]_&]:cursor-e-resize [[data-side=right][data-state=collapsed]_&]:cursor-w-resize",
        "hover:group-data-[collapsible=offcanvas]:bg-sidebar group-data-[collapsible=offcanvas]:translate-x-0 group-data-[collapsible=offcanvas]:after:left-full",
        "[[data-side=left][data-collapsible=offcanvas]_&]:-right-2",
        "[[data-side=right][data-collapsible=offcanvas]_&]:-left-2",
        className
      ),
      ...props
    }
  );
}
function SidebarInset({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx45(
    "main",
    {
      "data-slot": "sidebar-inset",
      className: cn(
        "bg-background relative flex w-full flex-1 flex-col",
        "md:peer-data-[variant=inset]:m-2 md:peer-data-[variant=inset]:ml-0 md:peer-data-[variant=inset]:rounded-xl md:peer-data-[variant=inset]:shadow-sm md:peer-data-[variant=inset]:peer-data-[state=collapsed]:ml-2",
        className
      ),
      ...props
    }
  );
}
function SidebarInput({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx45(
    Input,
    {
      "data-slot": "sidebar-input",
      "data-sidebar": "input",
      className: cn("bg-background h-8 w-full shadow-none", className),
      ...props
    }
  );
}
function SidebarHeader({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx45(
    "div",
    {
      "data-slot": "sidebar-header",
      "data-sidebar": "header",
      className: cn("flex flex-col gap-2 p-2", className),
      ...props
    }
  );
}
function SidebarFooter({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx45(
    "div",
    {
      "data-slot": "sidebar-footer",
      "data-sidebar": "footer",
      className: cn("flex flex-col gap-2 p-2", className),
      ...props
    }
  );
}
function SidebarSeparator({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx45(
    Separator5,
    {
      "data-slot": "sidebar-separator",
      "data-sidebar": "separator",
      className: cn("bg-sidebar-border mx-2 w-auto", className),
      ...props
    }
  );
}
function SidebarContent({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx45(
    "div",
    {
      "data-slot": "sidebar-content",
      "data-sidebar": "content",
      className: cn(
        "flex min-h-0 flex-1 flex-col gap-2 overflow-auto group-data-[collapsible=icon]:overflow-hidden",
        className
      ),
      ...props
    }
  );
}
function SidebarGroup({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx45(
    "div",
    {
      "data-slot": "sidebar-group",
      "data-sidebar": "group",
      className: cn("relative flex w-full min-w-0 flex-col p-2", className),
      ...props
    }
  );
}
function SidebarGroupLabel({
  className,
  asChild = false,
  ...props
}) {
  const Comp = asChild ? Slot6 : "div";
  return /* @__PURE__ */ jsx45(
    Comp,
    {
      "data-slot": "sidebar-group-label",
      "data-sidebar": "group-label",
      className: cn(
        "text-sidebar-foreground/70 ring-sidebar-ring flex h-8 shrink-0 items-center rounded-md px-2 text-xs font-medium outline-hidden transition-[margin,opacity] duration-200 ease-linear focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0",
        "group-data-[collapsible=icon]:-mt-8 group-data-[collapsible=icon]:opacity-0",
        className
      ),
      ...props
    }
  );
}
function SidebarGroupAction({
  className,
  asChild = false,
  ...props
}) {
  const Comp = asChild ? Slot6 : "button";
  return /* @__PURE__ */ jsx45(
    Comp,
    {
      "data-slot": "sidebar-group-action",
      "data-sidebar": "group-action",
      className: cn(
        "text-sidebar-foreground ring-sidebar-ring hover:bg-sidebar-accent hover:text-sidebar-accent-foreground absolute top-3.5 right-3 flex aspect-square w-5 items-center justify-center rounded-md p-0 outline-hidden transition-transform focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0",
        "after:absolute after:-inset-2 md:after:hidden",
        "group-data-[collapsible=icon]:hidden",
        className
      ),
      ...props
    }
  );
}
function SidebarGroupContent({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx45(
    "div",
    {
      "data-slot": "sidebar-group-content",
      "data-sidebar": "group-content",
      className: cn("w-full text-sm", className),
      ...props
    }
  );
}
function SidebarMenu({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx45(
    "ul",
    {
      "data-slot": "sidebar-menu",
      "data-sidebar": "menu",
      className: cn("flex w-full min-w-0 flex-col gap-1", className),
      ...props
    }
  );
}
function SidebarMenuItem({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx45(
    "li",
    {
      "data-slot": "sidebar-menu-item",
      "data-sidebar": "menu-item",
      className: cn("group/menu-item relative", className),
      ...props
    }
  );
}
function SidebarMenuButton({
  asChild = false,
  isActive = false,
  variant = "default",
  size = "default",
  tooltip,
  className,
  ...props
}) {
  const Comp = asChild ? Slot6 : "button";
  const { isMobile, state } = useSidebar();
  const button = /* @__PURE__ */ jsx45(
    Comp,
    {
      "data-slot": "sidebar-menu-button",
      "data-sidebar": "menu-button",
      "data-size": size,
      "data-active": isActive,
      className: cn(sidebarMenuButtonVariants({ variant, size }), className),
      ...props
    }
  );
  if (!tooltip) {
    return button;
  }
  if (typeof tooltip === "string") {
    tooltip = {
      children: tooltip
    };
  }
  return /* @__PURE__ */ jsxs26(Tooltip2, { children: [
    /* @__PURE__ */ jsx45(TooltipTrigger, { asChild: true, children: button }),
    /* @__PURE__ */ jsx45(
      TooltipContent,
      {
        side: "right",
        align: "center",
        hidden: state !== "collapsed" || isMobile,
        ...tooltip
      }
    )
  ] });
}
function SidebarMenuAction({
  className,
  asChild = false,
  showOnHover = false,
  ...props
}) {
  const Comp = asChild ? Slot6 : "button";
  return /* @__PURE__ */ jsx45(
    Comp,
    {
      "data-slot": "sidebar-menu-action",
      "data-sidebar": "menu-action",
      className: cn(
        "text-sidebar-foreground ring-sidebar-ring hover:bg-sidebar-accent hover:text-sidebar-accent-foreground peer-hover/menu-button:text-sidebar-accent-foreground absolute top-1.5 right-1 flex aspect-square w-5 items-center justify-center rounded-md p-0 outline-hidden transition-transform focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0",
        "after:absolute after:-inset-2 md:after:hidden",
        "peer-data-[size=sm]/menu-button:top-1",
        "peer-data-[size=default]/menu-button:top-1.5",
        "peer-data-[size=lg]/menu-button:top-2.5",
        "group-data-[collapsible=icon]:hidden",
        showOnHover && "peer-data-[active=true]/menu-button:text-sidebar-accent-foreground group-focus-within/menu-item:opacity-100 group-hover/menu-item:opacity-100 data-[state=open]:opacity-100 md:opacity-0",
        className
      ),
      ...props
    }
  );
}
function SidebarMenuBadge({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx45(
    "div",
    {
      "data-slot": "sidebar-menu-badge",
      "data-sidebar": "menu-badge",
      className: cn(
        "text-sidebar-foreground pointer-events-none absolute right-1 flex h-5 min-w-5 items-center justify-center rounded-md px-1 text-xs font-medium tabular-nums select-none",
        "peer-hover/menu-button:text-sidebar-accent-foreground peer-data-[active=true]/menu-button:text-sidebar-accent-foreground",
        "peer-data-[size=sm]/menu-button:top-1",
        "peer-data-[size=default]/menu-button:top-1.5",
        "peer-data-[size=lg]/menu-button:top-2.5",
        "group-data-[collapsible=icon]:hidden",
        className
      ),
      ...props
    }
  );
}
function SidebarMenuSkeleton({
  className,
  showIcon = false,
  ...props
}) {
  const width = React9.useMemo(() => {
    return `${Math.floor(Math.random() * 40) + 50}%`;
  }, []);
  return /* @__PURE__ */ jsxs26(
    "div",
    {
      "data-slot": "sidebar-menu-skeleton",
      "data-sidebar": "menu-skeleton",
      className: cn("flex h-8 items-center gap-2 rounded-md px-2", className),
      ...props,
      children: [
        showIcon && /* @__PURE__ */ jsx45(Skeleton, { className: "size-4 rounded-md", "data-sidebar": "menu-skeleton-icon", "aria-busy": "true" }),
        /* @__PURE__ */ jsx45(
          Skeleton,
          {
            className: "h-4 max-w-(--skeleton-width) flex-1",
            "data-sidebar": "menu-skeleton-text",
            "aria-busy": "true",
            style: {
              "--skeleton-width": width
            }
          }
        )
      ]
    }
  );
}
function SidebarMenuSub({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx45(
    "ul",
    {
      "data-slot": "sidebar-menu-sub",
      "data-sidebar": "menu-sub",
      className: cn(
        "border-sidebar-border mx-3.5 flex min-w-0 translate-x-px flex-col gap-1 border-l px-2.5 py-0.5",
        "group-data-[collapsible=icon]:hidden",
        className
      ),
      ...props
    }
  );
}
function SidebarMenuSubItem({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx45(
    "li",
    {
      "data-slot": "sidebar-menu-sub-item",
      "data-sidebar": "menu-sub-item",
      className: cn("group/menu-sub-item relative", className),
      ...props
    }
  );
}
function SidebarMenuSubButton({
  asChild = false,
  size = "md",
  isActive = false,
  className,
  ...props
}) {
  const Comp = asChild ? Slot6 : "a";
  return /* @__PURE__ */ jsx45(
    Comp,
    {
      "data-slot": "sidebar-menu-sub-button",
      "data-sidebar": "menu-sub-button",
      "data-size": size,
      "data-active": isActive,
      className: cn(
        "text-sidebar-foreground ring-sidebar-ring hover:bg-sidebar-accent hover:text-sidebar-accent-foreground active:bg-sidebar-accent active:text-sidebar-accent-foreground [&>svg]:text-sidebar-accent-foreground flex h-7 min-w-0 -translate-x-px items-center gap-2 overflow-hidden rounded-md px-2 outline-hidden focus-visible:ring-2 disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0",
        "data-[active=true]:bg-sidebar-accent data-[active=true]:text-sidebar-accent-foreground",
        size === "sm" && "text-xs",
        size === "md" && "text-sm",
        "group-data-[collapsible=icon]:hidden",
        className
      ),
      ...props
    }
  );
}

// src/components/ui/slider.tsx
import * as React10 from "react";
import * as SliderPrimitive from "@radix-ui/react-slider";
import { jsx as jsx46, jsxs as jsxs27 } from "react/jsx-runtime";
function Slider({
  className,
  defaultValue,
  value,
  min = 0,
  max = 100,
  thumbLabel,
  ...props
}) {
  const _values = React10.useMemo(() => Array.isArray(value) ? value : Array.isArray(defaultValue) ? defaultValue : [min, max], [value, defaultValue, min, max]);
  return /* @__PURE__ */ jsxs27(
    SliderPrimitive.Root,
    {
      "data-slot": "slider",
      defaultValue,
      value,
      min,
      max,
      className: cn(
        "relative flex w-full touch-none items-center select-none data-[disabled]:opacity-50 data-[orientation=vertical]:h-full data-[orientation=vertical]:min-h-44 data-[orientation=vertical]:w-auto data-[orientation=vertical]:flex-col",
        className
      ),
      ...props,
      children: [
        /* @__PURE__ */ jsx46(
          SliderPrimitive.Track,
          {
            "data-slot": "slider-track",
            className: cn(
              "bg-muted relative grow overflow-hidden rounded-full data-[orientation=horizontal]:h-1.5 data-[orientation=horizontal]:w-full data-[orientation=vertical]:h-full data-[orientation=vertical]:w-1.5"
            ),
            children: /* @__PURE__ */ jsx46(
              SliderPrimitive.Range,
              {
                "data-slot": "slider-range",
                className: cn(
                  "bg-primary absolute data-[orientation=horizontal]:h-full data-[orientation=vertical]:w-full"
                )
              }
            )
          }
        ),
        Array.from({ length: _values.length }, (_, index) => /* @__PURE__ */ jsx46(
          SliderPrimitive.Thumb,
          {
            "data-slot": "slider-thumb",
            "aria-label": Array.isArray(thumbLabel) ? thumbLabel[index] ?? `Slider thumb ${index + 1}` : thumbLabel,
            className: "border-primary bg-background ring-ring/50 block size-4 shrink-0 rounded-full border shadow-sm transition-[color,box-shadow] hover:ring-4 focus-visible:ring-4 focus-visible:outline-hidden disabled:pointer-events-none disabled:opacity-50"
          },
          index
        ))
      ]
    }
  );
}

// src/components/ui/sonner.tsx
import { useTheme } from "next-themes";
import { Toaster as Sonner } from "sonner";
import { jsx as jsx47 } from "react/jsx-runtime";
var Toaster = ({
  ...props
}) => {
  const { theme = "system" } = useTheme();
  return /* @__PURE__ */ jsx47(
    Sonner,
    {
      theme,
      className: "toaster group",
      style: {
        "--normal-bg": "var(--popover)",
        "--normal-text": "var(--popover-foreground)",
        "--normal-border": "var(--border)"
      },
      ...props
    }
  );
};

// src/components/ui/status-badge.tsx
import { cva as cva8 } from "class-variance-authority";
import { Activity, AlertTriangle, CheckCircle, Clock, XCircle } from "lucide-react";
import { jsx as jsx48, jsxs as jsxs28 } from "react/jsx-runtime";
var statusBadgeVariants = cva8(
  "inline-flex items-center justify-center gap-1 rounded-md border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      status: {
        completed: "border-green-300 bg-green-100 text-green-800 dark:border-green-700 dark:bg-green-900/30 dark:text-green-400",
        running: "border-blue-300 bg-blue-100 text-blue-800 dark:border-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
        failed: "border-red-300 bg-red-100 text-red-800 dark:border-red-700 dark:bg-red-900/30 dark:text-red-400",
        queued: "border-gray-300 bg-gray-100 text-gray-800 dark:border-gray-700 dark:bg-gray-900/30 dark:text-gray-400",
        assigned: "border-yellow-300 bg-yellow-100 text-yellow-800 dark:border-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
        cancelled: "border-orange-300 bg-orange-100 text-orange-800 dark:border-orange-700 dark:bg-orange-900/30 dark:text-orange-400"
      }
    },
    defaultVariants: {
      status: "queued"
    }
  }
);
var getStatusIcon = (status) => {
  switch (status) {
    case "completed":
      return /* @__PURE__ */ jsx48(CheckCircle, { className: "w-3 h-3" });
    case "running":
      return /* @__PURE__ */ jsx48(Activity, { className: "w-3 h-3 motion-safe:animate-pulse motion-reduce:animate-none" });
    case "failed":
      return /* @__PURE__ */ jsx48(XCircle, { className: "w-3 h-3" });
    case "queued":
    case "assigned":
      return /* @__PURE__ */ jsx48(Clock, { className: "w-3 h-3" });
    case "cancelled":
      return /* @__PURE__ */ jsx48(AlertTriangle, { className: "w-3 h-3" });
    default:
      return /* @__PURE__ */ jsx48(Activity, { className: "w-3 h-3" });
  }
};
function StatusBadge({
  className,
  status = "queued",
  showIcon = true,
  icon,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsxs28(
    "span",
    {
      className: cn(statusBadgeVariants({ status }), className),
      ...props,
      children: [
        showIcon && (icon || getStatusIcon(status)),
        children
      ]
    }
  );
}

// src/components/ui/switch.tsx
import * as SwitchPrimitives from "@radix-ui/react-switch";
import { jsx as jsx49 } from "react/jsx-runtime";
function Switch({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx49(
    SwitchPrimitives.Root,
    {
      "data-slot": "switch",
      className: cn(
        "focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive peer inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=unchecked]:bg-input",
        className
      ),
      ...props,
      children: /* @__PURE__ */ jsx49(
        SwitchPrimitives.Thumb,
        {
          "data-slot": "switch-thumb",
          className: cn(
            "bg-background pointer-events-none block size-4 rounded-full shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0"
          )
        }
      )
    }
  );
}

// src/components/ui/table.tsx
import { jsx as jsx50 } from "react/jsx-runtime";
function Table({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx50("div", { "data-slot": "table-container", className: "relative w-full overflow-x-auto", children: /* @__PURE__ */ jsx50(
    "table",
    {
      "data-slot": "table",
      className: cn("w-full caption-bottom text-sm", className),
      ...props
    }
  ) });
}
function TableHeader({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx50(
    "thead",
    {
      "data-slot": "table-header",
      className: cn("[&_tr]:border-b", className),
      ...props
    }
  );
}
function TableBody({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx50(
    "tbody",
    {
      "data-slot": "table-body",
      className: cn("[&_tr:last-child]:border-0", className),
      ...props
    }
  );
}
function TableFooter({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx50(
    "tfoot",
    {
      "data-slot": "table-footer",
      className: cn("bg-muted/50 border-t font-medium [&>tr]:last:border-b-0", className),
      ...props
    }
  );
}
function TableRow({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx50(
    "tr",
    {
      "data-slot": "table-row",
      className: cn(
        "hover:bg-muted/50 data-[state=selected]:bg-muted border-b transition-colors",
        className
      ),
      ...props
    }
  );
}
function TableHead({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx50(
    "th",
    {
      "data-slot": "table-head",
      scope: "col",
      className: cn(
        "text-foreground h-10 px-2 text-left align-middle font-medium whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
        className
      ),
      ...props
    }
  );
}
function TableCell({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx50(
    "td",
    {
      "data-slot": "table-cell",
      className: cn(
        "p-2 align-middle whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
        className
      ),
      ...props
    }
  );
}
function TableCaption({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx50(
    "caption",
    {
      "data-slot": "table-caption",
      className: cn("text-muted-foreground mt-4 text-sm", className),
      ...props
    }
  );
}

// src/components/ui/tabs.tsx
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { jsx as jsx51 } from "react/jsx-runtime";
function Tabs({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx51(
    TabsPrimitive.Root,
    {
      "data-slot": "tabs",
      className: cn("flex flex-col gap-2", className),
      ...props
    }
  );
}
function TabsList({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx51(
    TabsPrimitive.List,
    {
      "data-slot": "tabs-list",
      className: cn(
        "bg-muted text-muted-foreground inline-flex h-9 w-fit items-center justify-center rounded-lg p-[3px]",
        className
      ),
      ...props
    }
  );
}
function TabsTrigger({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx51(
    TabsPrimitive.Trigger,
    {
      "data-slot": "tabs-trigger",
      className: cn(
        "data-[state=active]:bg-background dark:data-[state=active]:text-foreground focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:outline-ring dark:data-[state=active]:border-input dark:data-[state=active]:bg-input/30 text-foreground dark:text-muted-foreground inline-flex h-[calc(100%-1px)] flex-1 items-center justify-center gap-1.5 rounded-md border border-transparent px-2 py-1 text-sm font-medium whitespace-nowrap transition-[color,box-shadow] focus-visible:ring-[3px] focus-visible:outline-1 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:shadow-sm [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      ),
      ...props
    }
  );
}
function TabsContent({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx51(
    TabsPrimitive.Content,
    {
      "data-slot": "tabs-content",
      className: cn("flex-1 outline-none", className),
      ...props
    }
  );
}

// src/components/ui/textarea.tsx
import { jsx as jsx52 } from "react/jsx-runtime";
function Textarea({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx52(
    "textarea",
    {
      "data-slot": "textarea",
      className: cn(
        "border-input placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 flex field-sizing-content min-h-16 w-full rounded-md border bg-transparent px-3 py-2 text-base shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
        className
      ),
      ...props
    }
  );
}

// src/components/ui/toggle-group.tsx
import * as React11 from "react";
import * as ToggleGroupPrimitive from "@radix-ui/react-toggle-group";

// src/components/ui/toggle.tsx
import * as TogglePrimitive from "@radix-ui/react-toggle";
import { cva as cva9 } from "class-variance-authority";
import { jsx as jsx53 } from "react/jsx-runtime";
var toggleVariants = cva9(
  "inline-flex items-center justify-center gap-2 rounded-md text-sm font-medium hover:bg-muted hover:text-muted-foreground disabled:pointer-events-none disabled:opacity-50 data-[state=on]:bg-accent data-[state=on]:text-accent-foreground [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 [&_svg]:shrink-0 focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] outline-none transition-[color,box-shadow] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive whitespace-nowrap",
  {
    variants: {
      variant: {
        default: "bg-transparent",
        outline: "border border-input bg-transparent shadow-xs hover:bg-accent hover:text-accent-foreground"
      },
      size: {
        default: "h-9 px-2 min-w-9",
        sm: "h-8 px-1.5 min-w-8",
        lg: "h-10 px-2.5 min-w-10"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default"
    }
  }
);
function Toggle({
  className,
  variant,
  size,
  ...props
}) {
  return /* @__PURE__ */ jsx53(
    TogglePrimitive.Root,
    {
      "data-slot": "toggle",
      className: cn(toggleVariants({ variant, size, className })),
      ...props
    }
  );
}

// src/components/ui/toggle-group.tsx
import { jsx as jsx54 } from "react/jsx-runtime";
var ToggleGroupContext = React11.createContext({
  size: "default",
  variant: "default"
});
function ToggleGroup({
  className,
  variant,
  size,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsx54(
    ToggleGroupPrimitive.Root,
    {
      "data-slot": "toggle-group",
      "data-variant": variant,
      "data-size": size,
      className: cn(
        "group/toggle-group flex w-fit items-center rounded-md data-[variant=outline]:shadow-xs",
        className
      ),
      ...props,
      children: /* @__PURE__ */ jsx54(ToggleGroupContext.Provider, { value: { variant, size }, children })
    }
  );
}
function ToggleGroupItem({
  className,
  children,
  variant,
  size,
  ...props
}) {
  const context = React11.useContext(ToggleGroupContext);
  return /* @__PURE__ */ jsx54(
    ToggleGroupPrimitive.Item,
    {
      "data-slot": "toggle-group-item",
      "data-variant": context.variant || variant,
      "data-size": context.size || size,
      className: cn(toggleVariants({
        variant: context.variant || variant,
        size: context.size || size
      }), "min-w-0 flex-1 shrink-0 rounded-none shadow-none first:rounded-l-md last:rounded-r-md focus:z-10 focus-visible:z-10 data-[variant=outline]:border-l-0 data-[variant=outline]:first:border-l", className),
      ...props,
      children
    }
  );
}

// src/components/dashboard/stat-card.tsx
import * as React12 from "react";
import { jsx as jsx55, jsxs as jsxs29 } from "react/jsx-runtime";
var variantIconColors = {
  default: "text-[var(--neutral-600)]",
  blue: "text-[var(--primary-600)]",
  green: "text-[var(--success-600)]",
  yellow: "text-[var(--warning-600)]",
  red: "text-[var(--error-600)]",
  purple: "text-[var(--color-accent-600)]"
};
function StatCard({
  label,
  value,
  trend,
  badge,
  className,
  icon,
  deltaLabel,
  deltaPositive = true,
  variant = "default"
}) {
  const displayDelta = deltaLabel || trend;
  return /* @__PURE__ */ jsx55(
    "div",
    {
      className: cn(
        "rounded-xl border border-[var(--border)] bg-[var(--surface)] p-4 shadow-card",
        className
      ),
      children: /* @__PURE__ */ jsxs29("div", { className: "flex items-center justify-between", children: [
        /* @__PURE__ */ jsxs29("div", { className: "flex-1", children: [
          /* @__PURE__ */ jsxs29("div", { className: "flex items-center justify-between mb-2", children: [
            /* @__PURE__ */ jsx55("span", { className: "text-xs text-[var(--text-secondary)] font-medium", children: label }),
            badge && /* @__PURE__ */ jsx55("span", { className: "px-2 py-0.5 text-[10px] rounded-full bg-[var(--brand-50)] text-[var(--brand-700)]", children: badge })
          ] }),
          /* @__PURE__ */ jsx55("div", { className: "text-2xl font-semibold text-[var(--text-primary)]", children: value }),
          displayDelta && /* @__PURE__ */ jsx55(
            "div",
            {
              className: cn(
                "mt-1 text-xs",
                deltaPositive === "neutral" ? "text-[var(--text-secondary)]" : deltaPositive ? "text-[var(--success-600)]" : "text-[var(--error-600)]"
              ),
              children: displayDelta
            }
          )
        ] }),
        icon && /* @__PURE__ */ jsx55("div", { className: "ml-3 shrink-0", children: React12.isValidElement(icon) ? React12.cloneElement(icon, {
          className: cn("size-5", variantIconColors[variant])
        }) : icon })
      ] })
    }
  );
}

// src/components/dashboard/status-card.tsx
import * as React13 from "react";
import { cva as cva10 } from "class-variance-authority";
import { jsx as jsx56, jsxs as jsxs30 } from "react/jsx-runtime";
var statusCardVariants = cva10(
  // Base styles
  [
    "relative min-w-[140px] h-24 w-full",
    "text-left rounded-xl border px-4 py-3",
    "flex flex-col justify-between",
    "transition-all duration-150",
    "border-[var(--border)] bg-[var(--surface)] shadow-sm",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--a11y-focus-outline-color,var(--accessibility-focus-outline-color,#0284c7))] focus-visible:ring-offset-2"
  ],
  {
    variants: {
      variant: {
        default: "",
        blue: "",
        green: "",
        yellow: "",
        red: ""
      },
      isActive: {
        true: "shadow-md",
        false: "hover:shadow-md hover:border-[var(--neutral-300)]"
      },
      disabled: {
        true: "opacity-50 cursor-not-allowed",
        false: "cursor-pointer"
      }
    },
    compoundVariants: [
      // Active state backgrounds and borders
      {
        variant: "default",
        isActive: true,
        className: "bg-neutral-50 border-neutral-300"
      },
      {
        variant: "blue",
        isActive: true,
        className: "bg-primary-50 border-primary-500"
      },
      {
        variant: "green",
        isActive: true,
        className: "bg-success-50 border-success-500"
      },
      {
        variant: "yellow",
        isActive: true,
        className: "bg-warning-50 border-warning-500"
      },
      {
        variant: "red",
        isActive: true,
        className: "bg-error-50 border-error-500"
      }
    ],
    defaultVariants: {
      variant: "default",
      isActive: false,
      disabled: false
    }
  }
);
var iconContainerVariants = cva10(
  [
    "flex items-center justify-center shrink-0",
    "h-[var(--card-icon-status-containerSize,28px)] w-[var(--card-icon-status-containerSize,28px)]",
    "rounded-[var(--card-icon-status-shape,8px)]"
  ],
  {
    variants: {
      variant: {
        default: "bg-[var(--neutral-100)] text-[var(--neutral-600)]",
        blue: "bg-[var(--primary-50)] text-[var(--primary-600)]",
        green: "bg-[var(--success-50)] text-[var(--success-600)]",
        yellow: "bg-[var(--warning-50)] text-[var(--warning-600)]",
        red: "bg-[var(--error-50)] text-[var(--error-600)]"
      },
      isActive: {
        true: "",
        false: ""
      }
    },
    compoundVariants: [
      { variant: "default", isActive: true, className: "bg-[var(--neutral-200)]" },
      { variant: "blue", isActive: true, className: "bg-[var(--primary-100)]" },
      { variant: "green", isActive: true, className: "bg-[var(--success-100)]" },
      { variant: "yellow", isActive: true, className: "bg-[var(--warning-100)]" },
      { variant: "red", isActive: true, className: "bg-[var(--error-100)]" }
    ],
    defaultVariants: {
      variant: "default",
      isActive: false
    }
  }
);
var valueVariants = cva10(
  "text-2xl font-semibold transition-colors duration-150",
  {
    variants: {
      variant: {
        default: "",
        blue: "",
        green: "",
        yellow: "",
        red: ""
      },
      isActive: {
        true: "",
        false: "text-[var(--text-primary)]"
      }
    },
    compoundVariants: [
      {
        variant: "default",
        isActive: true,
        className: "text-[var(--neutral-700)]"
      },
      {
        variant: "blue",
        isActive: true,
        className: "text-[var(--primary-600)]"
      },
      {
        variant: "green",
        isActive: true,
        className: "text-[var(--success-600)]"
      },
      {
        variant: "yellow",
        isActive: true,
        className: "text-[var(--warning-600)]"
      },
      {
        variant: "red",
        isActive: true,
        className: "text-[var(--error-600)]"
      }
    ],
    defaultVariants: {
      variant: "default",
      isActive: false
    }
  }
);
var highlightBarVariants = cva10(
  "pointer-events-none absolute inset-y-3 left-1.5 w-0.5 rounded-full",
  {
    variants: {
      variant: {
        default: "bg-[var(--neutral-400)]",
        blue: "bg-[var(--primary-600)]",
        green: "bg-[var(--success-600)]",
        yellow: "bg-[var(--warning-600)]",
        red: "bg-[var(--error-600)]"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);
function StatusCard({
  label,
  value,
  icon,
  variant = "default",
  isActive = false,
  disabled = false,
  tooltip,
  className,
  onClick,
  ...props
}) {
  return /* @__PURE__ */ jsxs30(
    "button",
    {
      type: "button",
      "aria-pressed": isActive,
      "aria-disabled": disabled,
      title: tooltip,
      onClick: disabled ? void 0 : onClick,
      className: cn(
        statusCardVariants({ variant, isActive, disabled }),
        className
      ),
      ...props,
      children: [
        isActive && /* @__PURE__ */ jsx56(
          "div",
          {
            className: highlightBarVariants({ variant }),
            "aria-hidden": "true"
          }
        ),
        /* @__PURE__ */ jsxs30("div", { className: "flex items-center justify-between", children: [
          /* @__PURE__ */ jsx56("span", { className: "text-xs font-medium text-[var(--text-secondary)]", children: label }),
          /* @__PURE__ */ jsx56("div", { className: iconContainerVariants({ variant, isActive }), children: icon && React13.isValidElement(icon) ? React13.cloneElement(icon, {
            className: "w-[var(--card-icon-status-size,16px)] h-[var(--card-icon-status-size,16px)]",
            "aria-hidden": "true"
          }) : icon })
        ] }),
        /* @__PURE__ */ jsx56("div", { className: "flex items-baseline", children: /* @__PURE__ */ jsx56("span", { className: valueVariants({ variant, isActive }), children: value }) })
      ]
    }
  );
}

// src/components/dashboard/timeline-list.tsx
import { jsx as jsx57, jsxs as jsxs31 } from "react/jsx-runtime";
function TimelineList({ items, className }) {
  return /* @__PURE__ */ jsx57("ul", { className: cn("space-y-4 text-sm", className), children: items.map((item) => /* @__PURE__ */ jsxs31("li", { className: "flex justify-between", children: [
    /* @__PURE__ */ jsxs31("div", { children: [
      /* @__PURE__ */ jsx57("div", { className: "font-medium text-[var(--text-primary)]", children: item.title }),
      /* @__PURE__ */ jsx57("div", { className: "text-xs text-[var(--text-secondary)]", children: item.desc })
    ] }),
    /* @__PURE__ */ jsx57("span", { className: "text-xs text-[var(--text-secondary)]", children: item.time })
  ] }, item.id)) });
}

// src/components/dashboard/system-status-list.tsx
import { jsx as jsx58, jsxs as jsxs32 } from "react/jsx-runtime";
var statusStyles = {
  Healthy: "bg-[var(--success-50)] text-[var(--success-600)]",
  Operational: "bg-[var(--success-50)] text-[var(--success-600)]",
  Degraded: "bg-warning-50 text-warning-600",
  Down: "bg-danger-50 text-danger-600"
};
var defaultStatusStyle = "bg-[var(--surface-muted)] text-[var(--text-secondary)]";
function getStatusStyle(status) {
  return statusStyles[status] || defaultStatusStyle;
}
function SystemStatusList({ items, className }) {
  return /* @__PURE__ */ jsx58("div", { className: cn("space-y-3", className), children: items.map((item) => /* @__PURE__ */ jsxs32(
    "div",
    {
      className: "flex items-center justify-between rounded-lg bg-[var(--surface-muted)] px-3 py-2",
      children: [
        /* @__PURE__ */ jsxs32("div", { children: [
          /* @__PURE__ */ jsx58("div", { className: "text-xs font-medium text-[var(--text-primary)]", children: item.service }),
          /* @__PURE__ */ jsx58("div", { className: "text-[10px] text-[var(--text-secondary)]", children: item.latency })
        ] }),
        /* @__PURE__ */ jsx58("span", { className: cn("rounded-full px-2 py-0.5 text-[10px] font-medium", getStatusStyle(item.status)), children: item.status })
      ]
    },
    item.service
  )) });
}

// src/components/dashboard/progress-track.tsx
import { jsx as jsx59, jsxs as jsxs33 } from "react/jsx-runtime";
function ProgressTrack({ items, className }) {
  return /* @__PURE__ */ jsx59("div", { className: cn("space-y-5", className), children: items.map((item) => {
    const clampedValue = Math.min(100, Math.max(0, item.value));
    return /* @__PURE__ */ jsxs33("div", { className: "space-y-1", children: [
      /* @__PURE__ */ jsxs33("div", { className: "flex items-center justify-between", children: [
        /* @__PURE__ */ jsx59("span", { className: "text-xs font-medium text-[var(--text-primary)]", children: item.label }),
        /* @__PURE__ */ jsxs33("span", { className: "text-xs text-[var(--text-secondary)]", children: [
          clampedValue,
          "%"
        ] })
      ] }),
      /* @__PURE__ */ jsx59("div", { className: "h-1.5 w-full overflow-hidden rounded-full bg-[var(--surface-muted)]", children: /* @__PURE__ */ jsx59(
        "div",
        {
          className: "h-full rounded-full bg-[var(--brand-500)]",
          style: { width: `${clampedValue}%` }
        }
      ) })
    ] }, item.label);
  }) });
}

// src/components/dashboard/metric-card.tsx
import * as React14 from "react";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { jsx as jsx60, jsxs as jsxs34 } from "react/jsx-runtime";
var variantIconColors2 = {
  default: "text-[var(--neutral-600)]",
  blue: "text-[var(--primary-600)]",
  green: "text-[var(--success-600)]",
  yellow: "text-[var(--warning-600)]",
  red: "text-[var(--error-600)]",
  purple: "text-[var(--color-accent-600)]"
};
var trendColors = {
  up: "text-[var(--success-600)]",
  down: "text-[var(--error-600)]",
  stable: "text-[var(--neutral-500)]"
};
var defaultTrendLabels = {
  up: "Increasing",
  down: "Decreasing",
  stable: "Stable"
};
var defaultTrendAriaLabels = {
  up: "Trending up",
  down: "Trending down",
  stable: "Stable"
};
function MetricCard({
  title,
  value,
  unit,
  icon,
  trend,
  trendLabels,
  trendAriaLabels,
  description,
  progress,
  variant = "default",
  className
}) {
  const trendData = React14.useMemo(() => {
    if (!trend) return null;
    const iconClass = cn("size-4", trendColors[trend]);
    const label = trendLabels?.[trend] ?? defaultTrendLabels[trend];
    const ariaLabel = trendAriaLabels?.[trend] ?? defaultTrendAriaLabels[trend];
    const TrendIcon = trend === "up" ? TrendingUp : trend === "down" ? TrendingDown : Minus;
    return {
      icon: /* @__PURE__ */ jsx60(TrendIcon, { className: iconClass, "aria-label": ariaLabel }),
      label,
      colorClass: trendColors[trend]
    };
  }, [trend, trendLabels, trendAriaLabels]);
  const formattedValue = React14.useMemo(
    () => typeof value === "number" ? Number.isInteger(value) ? value.toString() : value.toFixed(2) : value,
    [value]
  );
  return /* @__PURE__ */ jsxs34(Card, { className: cn("shadow-card", className), children: [
    /* @__PURE__ */ jsxs34(CardHeader, { className: "flex flex-row items-center justify-between space-y-0 pb-2", children: [
      /* @__PURE__ */ jsx60(CardTitle, { className: "text-sm font-medium text-[var(--text-secondary)]", children: title }),
      icon && /* @__PURE__ */ jsx60("div", { className: "shrink-0", children: React14.isValidElement(icon) ? React14.cloneElement(icon, {
        className: cn("size-5", variantIconColors2[variant])
      }) : icon })
    ] }),
    /* @__PURE__ */ jsxs34(CardContent, { children: [
      /* @__PURE__ */ jsxs34("div", { className: "text-2xl font-bold text-[var(--text-primary)]", children: [
        formattedValue,
        unit && /* @__PURE__ */ jsx60("span", { className: "ml-1 text-lg font-normal text-[var(--text-secondary)]", children: unit })
      ] }),
      description && /* @__PURE__ */ jsx60("p", { className: "mt-1 text-xs text-[var(--text-secondary)]", children: description }),
      trendData && /* @__PURE__ */ jsxs34("div", { className: "mt-2 flex items-center gap-1", children: [
        trendData.icon,
        /* @__PURE__ */ jsx60("span", { className: cn("text-xs", trendData.colorClass), children: trendData.label })
      ] }),
      typeof progress === "number" && /* @__PURE__ */ jsx60("div", { className: "mt-3", "aria-hidden": "true", children: /* @__PURE__ */ jsx60(Progress, { value: progress, className: "h-1.5" }) })
    ] })
  ] });
}

// src/components/dashboard/settings-card.tsx
import * as React15 from "react";
import { jsx as jsx61, jsxs as jsxs35 } from "react/jsx-runtime";
var variantIconColors3 = {
  default: "text-[var(--neutral-600)]",
  blue: "text-[var(--primary-600)]",
  green: "text-[var(--success-600)]",
  yellow: "text-[var(--warning-600)]",
  red: "text-[var(--error-600)]",
  purple: "text-[var(--color-accent-600)]"
};
function SettingsCard({
  title,
  description,
  icon,
  variant = "default",
  children,
  className,
  noPadding = false
}) {
  return /* @__PURE__ */ jsxs35(
    Card,
    {
      className: cn(
        "rounded-xl border border-[var(--border)] bg-[var(--surface)] shadow-card",
        className
      ),
      children: [
        /* @__PURE__ */ jsxs35(CardHeader, { className: "px-5 py-4", children: [
          /* @__PURE__ */ jsxs35("h3", { className: "flex items-center gap-2 text-base font-semibold text-[var(--text-primary)]", children: [
            icon && /* @__PURE__ */ jsx61("span", { className: "shrink-0", children: React15.isValidElement(icon) ? React15.cloneElement(icon, {
              className: cn(
                "size-5",
                variantIconColors3[variant],
                icon.props.className
              )
            }) : icon }),
            title
          ] }),
          description && /* @__PURE__ */ jsx61(CardDescription, { className: "text-sm text-[var(--text-secondary)]", children: description })
        ] }),
        children && /* @__PURE__ */ jsx61(CardContent, { className: cn(noPadding ? "p-0" : "space-y-4 p-5 pt-0"), children })
      ]
    }
  );
}

// src/components/iotask/avatar-stack.tsx
import { jsx as jsx62, jsxs as jsxs36 } from "react/jsx-runtime";
var sizeStyles = {
  sm: "h-6 w-6 text-[10px]",
  md: "h-8 w-8 text-xs",
  lg: "h-10 w-10 text-sm"
};
var overlapStyles = {
  sm: "-ml-2",
  md: "-ml-2.5",
  lg: "-ml-3"
};
function AvatarStack({
  avatars,
  max = 4,
  size = "md",
  className
}) {
  const visibleAvatars = avatars.slice(0, max);
  const remainingCount = avatars.length - max;
  return /* @__PURE__ */ jsxs36("div", { className: cn("flex items-center", className), children: [
    visibleAvatars.map((avatar, index) => /* @__PURE__ */ jsx62(
      "div",
      {
        className: cn(
          "relative rounded-full border-2 border-[var(--surface)] bg-[var(--neutral-100)]",
          sizeStyles[size],
          index > 0 && overlapStyles[size]
        ),
        title: avatar.name,
        children: avatar.src ? /* @__PURE__ */ jsx62(
          "img",
          {
            src: avatar.src,
            alt: avatar.name,
            className: "h-full w-full rounded-full object-cover"
          }
        ) : /* @__PURE__ */ jsx62("div", { className: "flex h-full w-full items-center justify-center rounded-full bg-[var(--primary-100)] text-[var(--primary-700)] font-medium", children: getInitials(avatar.name) })
      },
      avatar.id
    )),
    remainingCount > 0 && /* @__PURE__ */ jsxs36(
      "div",
      {
        className: cn(
          "relative flex items-center justify-center rounded-full border-2 border-[var(--surface)] bg-[var(--neutral-200)] text-[var(--text-secondary)] font-medium",
          sizeStyles[size],
          overlapStyles[size]
        ),
        children: [
          "+",
          remainingCount
        ]
      }
    )
  ] });
}

// src/components/iotask/task-row.tsx
import { jsx as jsx63, jsxs as jsxs37 } from "react/jsx-runtime";
var statusStyles2 = {
  pending: "border-[var(--neutral-300)] bg-[var(--surface)]",
  in_progress: "border-[var(--primary-500)] bg-[var(--primary-50)]",
  completed: "border-[var(--success-500)] bg-[var(--success-50)]"
};
var priorityStyles = {
  low: "bg-[var(--neutral-100)] text-[var(--neutral-600)]",
  medium: "bg-[var(--warning-50)] text-[var(--warning-700)]",
  high: "bg-[var(--error-50)] text-[var(--error-700)]"
};
function TaskRow({
  id,
  title,
  status,
  priority,
  progress,
  dueDate,
  assignee,
  onStatusChange,
  className
}) {
  const handleCheckboxChange = () => {
    if (onStatusChange) {
      const newStatus = status === "completed" ? "pending" : "completed";
      onStatusChange(id, newStatus);
    }
  };
  return /* @__PURE__ */ jsxs37(
    "div",
    {
      className: cn(
        "flex items-center gap-3 rounded-lg border p-3 transition-colors hover:bg-[var(--neutral-50)]",
        statusStyles2[status],
        className
      ),
      children: [
        /* @__PURE__ */ jsx63(
          "button",
          {
            type: "button",
            role: "checkbox",
            "aria-checked": status === "completed",
            "aria-label": status === "completed" ? `Mark "${title}" as incomplete` : `Mark "${title}" as complete`,
            onClick: handleCheckboxChange,
            className: cn(
              "flex h-5 w-5 shrink-0 items-center justify-center rounded border-2 transition-colors",
              status === "completed" ? "border-[var(--success-500)] bg-[var(--success-500)] text-white" : "border-[var(--neutral-300)] bg-[var(--surface)]"
            ),
            children: status === "completed" && /* @__PURE__ */ jsx63(
              "svg",
              {
                className: "h-3 w-3",
                fill: "none",
                viewBox: "0 0 24 24",
                stroke: "currentColor",
                strokeWidth: 3,
                "aria-hidden": "true",
                children: /* @__PURE__ */ jsx63(
                  "path",
                  {
                    strokeLinecap: "round",
                    strokeLinejoin: "round",
                    d: "M5 13l4 4L19 7"
                  }
                )
              }
            )
          }
        ),
        /* @__PURE__ */ jsxs37("div", { className: "flex-1 min-w-0", children: [
          /* @__PURE__ */ jsx63(
            "div",
            {
              className: cn(
                "text-sm font-medium truncate",
                status === "completed" ? "text-[var(--text-secondary)] line-through" : "text-[var(--text-primary)]"
              ),
              children: title
            }
          ),
          progress !== void 0 && status !== "completed" && /* @__PURE__ */ jsx63("div", { className: "mt-1.5 h-1.5 w-full rounded-full bg-[var(--neutral-200)]", children: /* @__PURE__ */ jsx63(
            "div",
            {
              className: "h-full rounded-full bg-[var(--primary-500)] transition-all",
              style: { width: `${Math.min(100, Math.max(0, progress))}%` }
            }
          ) })
        ] }),
        /* @__PURE__ */ jsxs37("div", { className: "flex items-center gap-2 shrink-0", children: [
          priority && /* @__PURE__ */ jsx63(
            "span",
            {
              className: cn(
                "px-2 py-0.5 text-[10px] font-medium rounded-full uppercase",
                priorityStyles[priority]
              ),
              children: priority
            }
          ),
          dueDate && /* @__PURE__ */ jsx63("span", { className: "text-xs text-[var(--text-secondary)]", children: dueDate }),
          assignee && /* @__PURE__ */ jsx63(
            "div",
            {
              className: "flex h-6 w-6 items-center justify-center rounded-full bg-[var(--primary-100)] text-[10px] font-medium text-[var(--primary-700)]",
              title: assignee,
              children: getInitials(assignee)
            }
          )
        ] })
      ]
    }
  );
}

// src/components/iotask/task-list-section.tsx
import { jsx as jsx64, jsxs as jsxs38 } from "react/jsx-runtime";
function TaskListSection({
  title,
  count,
  action,
  children,
  className
}) {
  return /* @__PURE__ */ jsxs38(
    "div",
    {
      className: cn(
        "rounded-xl border border-[var(--border)] bg-[var(--surface)] shadow-card",
        className
      ),
      children: [
        /* @__PURE__ */ jsxs38("div", { className: "flex items-center justify-between border-b border-[var(--border)] px-5 py-4", children: [
          /* @__PURE__ */ jsxs38("div", { className: "flex items-center gap-2", children: [
            /* @__PURE__ */ jsx64("h2", { className: "text-sm font-semibold text-[var(--text-primary)]", children: title }),
            count !== void 0 && /* @__PURE__ */ jsx64("span", { className: "flex h-5 min-w-5 items-center justify-center rounded-full bg-[var(--neutral-100)] px-1.5 text-xs font-medium text-[var(--text-secondary)]", children: count })
          ] }),
          action && /* @__PURE__ */ jsx64("div", { className: "text-xs", children: action })
        ] }),
        /* @__PURE__ */ jsx64("div", { className: "space-y-2 p-4", children })
      ]
    }
  );
}

// src/components/iotask/activity-list-item.tsx
import { jsx as jsx65, jsxs as jsxs39 } from "react/jsx-runtime";
var typeStyles = {
  task: "bg-[var(--primary-50)] text-[var(--primary-600)]",
  comment: "bg-[var(--neutral-100)] text-[var(--neutral-600)]",
  update: "bg-[var(--success-50)] text-[var(--success-600)]",
  milestone: "bg-[var(--color-accent-50)] text-[var(--color-accent-600)]",
  alert: "bg-[var(--warning-50)] text-[var(--warning-600)]"
};
var defaultIcons = {
  task: /* @__PURE__ */ jsx65("svg", { className: "h-4 w-4", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", strokeWidth: 2, "aria-hidden": "true", children: /* @__PURE__ */ jsx65("path", { strokeLinecap: "round", strokeLinejoin: "round", d: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" }) }),
  comment: /* @__PURE__ */ jsx65("svg", { className: "h-4 w-4", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", strokeWidth: 2, "aria-hidden": "true", children: /* @__PURE__ */ jsx65("path", { strokeLinecap: "round", strokeLinejoin: "round", d: "M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" }) }),
  update: /* @__PURE__ */ jsx65("svg", { className: "h-4 w-4", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", strokeWidth: 2, "aria-hidden": "true", children: /* @__PURE__ */ jsx65("path", { strokeLinecap: "round", strokeLinejoin: "round", d: "M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" }) }),
  milestone: /* @__PURE__ */ jsx65("svg", { className: "h-4 w-4", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", strokeWidth: 2, "aria-hidden": "true", children: /* @__PURE__ */ jsx65("path", { strokeLinecap: "round", strokeLinejoin: "round", d: "M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" }) }),
  alert: /* @__PURE__ */ jsx65("svg", { className: "h-4 w-4", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", strokeWidth: 2, "aria-hidden": "true", children: /* @__PURE__ */ jsx65("path", { strokeLinecap: "round", strokeLinejoin: "round", d: "M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" }) })
};
function ActivityListItem({
  type,
  title,
  description,
  timestamp,
  icon,
  user,
  className
}) {
  return /* @__PURE__ */ jsxs39("div", { className: cn("flex gap-3 py-3", className), children: [
    /* @__PURE__ */ jsx65(
      "div",
      {
        className: cn(
          "flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
          typeStyles[type]
        ),
        children: icon || defaultIcons[type]
      }
    ),
    /* @__PURE__ */ jsxs39("div", { className: "flex-1 min-w-0", children: [
      /* @__PURE__ */ jsxs39("div", { className: "flex items-start justify-between gap-2", children: [
        /* @__PURE__ */ jsxs39("div", { className: "min-w-0", children: [
          /* @__PURE__ */ jsx65("p", { className: "text-sm font-medium text-[var(--text-primary)] truncate", children: title }),
          description && /* @__PURE__ */ jsx65("p", { className: "mt-0.5 text-xs text-[var(--text-secondary)] line-clamp-2", children: description })
        ] }),
        /* @__PURE__ */ jsx65("span", { className: "shrink-0 text-xs text-[var(--text-secondary)]", children: timestamp })
      ] }),
      user && /* @__PURE__ */ jsxs39("div", { className: "mt-2 flex items-center gap-1.5", children: [
        user.avatar ? /* @__PURE__ */ jsx65(
          "img",
          {
            src: user.avatar,
            alt: user.name,
            className: "h-4 w-4 rounded-full object-cover"
          }
        ) : /* @__PURE__ */ jsx65("div", { className: "flex h-4 w-4 items-center justify-center rounded-full bg-[var(--primary-100)] text-[8px] font-medium text-[var(--primary-700)]", children: getInitials(user.name) }),
        /* @__PURE__ */ jsx65("span", { className: "text-xs text-[var(--text-secondary)]", children: user.name })
      ] })
    ] })
  ] });
}

// src/components/iotask/activity-list-panel.tsx
import * as React16 from "react";
import { jsx as jsx66, jsxs as jsxs40 } from "react/jsx-runtime";
function ActivityListPanel({
  title,
  action,
  children,
  emptyMessage = "No recent activity",
  className
}) {
  const hasChildren = React16.Children.count(children) > 0;
  return /* @__PURE__ */ jsxs40(
    "div",
    {
      className: cn(
        "rounded-xl border border-[var(--border)] bg-[var(--surface)] shadow-card",
        className
      ),
      children: [
        /* @__PURE__ */ jsxs40("div", { className: "flex items-center justify-between border-b border-[var(--border)] px-5 py-4", children: [
          /* @__PURE__ */ jsx66("h2", { className: "text-sm font-semibold text-[var(--text-primary)]", children: title }),
          action && /* @__PURE__ */ jsx66("div", { className: "text-xs", children: action })
        ] }),
        /* @__PURE__ */ jsx66("div", { className: "divide-y divide-[var(--border)] px-5", children: hasChildren ? children : /* @__PURE__ */ jsx66("div", { className: "py-8 text-center text-sm text-[var(--text-secondary)]", children: emptyMessage }) })
      ]
    }
  );
}

// src/components/iotask/circle-progress-card.tsx
import { jsx as jsx67, jsxs as jsxs41 } from "react/jsx-runtime";
var sizeConfig = {
  sm: { size: 80, strokeWidth: 6, fontSize: "text-lg" },
  md: { size: 120, strokeWidth: 8, fontSize: "text-2xl" },
  lg: { size: 160, strokeWidth: 10, fontSize: "text-3xl" }
};
var colorStyles = {
  primary: "stroke-[var(--primary-500)]",
  success: "stroke-[var(--success-500)]",
  warning: "stroke-[var(--warning-500)]",
  error: "stroke-[var(--error-500)]",
  accent: "stroke-[var(--color-accent-500)]"
};
function CircleProgressCard({
  title,
  value,
  max = 100,
  label,
  subtitle,
  size = "md",
  color = "primary",
  className
}) {
  const config = sizeConfig[size];
  const radius2 = (config.size - config.strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius2;
  const percentage = Math.min(100, Math.max(0, value / max * 100));
  const strokeDashoffset = circumference - percentage / 100 * circumference;
  return /* @__PURE__ */ jsxs41(
    "div",
    {
      className: cn(
        "rounded-xl border border-[var(--border)] bg-[var(--surface)] p-5 shadow-card",
        className
      ),
      children: [
        /* @__PURE__ */ jsx67("h3", { className: "text-sm font-semibold text-[var(--text-primary)] mb-4", children: title }),
        /* @__PURE__ */ jsxs41("div", { className: "flex flex-col items-center", children: [
          /* @__PURE__ */ jsxs41("div", { className: "relative", style: { width: config.size, height: config.size }, children: [
            /* @__PURE__ */ jsxs41(
              "svg",
              {
                className: "transform -rotate-90",
                width: config.size,
                height: config.size,
                "aria-hidden": "true",
                children: [
                  /* @__PURE__ */ jsx67(
                    "circle",
                    {
                      cx: config.size / 2,
                      cy: config.size / 2,
                      r: radius2,
                      fill: "none",
                      stroke: "var(--neutral-200)",
                      strokeWidth: config.strokeWidth
                    }
                  ),
                  /* @__PURE__ */ jsx67(
                    "circle",
                    {
                      cx: config.size / 2,
                      cy: config.size / 2,
                      r: radius2,
                      fill: "none",
                      className: cn("transition-all duration-500", colorStyles[color]),
                      strokeWidth: config.strokeWidth,
                      strokeLinecap: "round",
                      strokeDasharray: circumference,
                      strokeDashoffset
                    }
                  )
                ]
              }
            ),
            /* @__PURE__ */ jsx67("div", { className: "absolute inset-0 flex flex-col items-center justify-center", children: /* @__PURE__ */ jsx67("span", { className: cn("font-bold text-[var(--text-primary)]", config.fontSize), children: label || `${Math.round(percentage)}%` }) })
          ] }),
          subtitle && /* @__PURE__ */ jsx67("p", { className: "mt-3 text-xs text-[var(--text-secondary)] text-center", children: subtitle })
        ] })
      ]
    }
  );
}

// src/components/iotask/calendar-card.tsx
import * as React17 from "react";
import { jsx as jsx68, jsxs as jsxs42 } from "react/jsx-runtime";
var eventColorStyles = {
  primary: "border-l-[var(--primary-500)] bg-[var(--primary-50)]",
  success: "border-l-[var(--success-500)] bg-[var(--success-50)]",
  warning: "border-l-[var(--warning-500)] bg-[var(--warning-50)]",
  error: "border-l-[var(--error-500)] bg-[var(--error-50)]",
  accent: "border-l-[var(--color-accent-500)] bg-[var(--color-accent-50)]"
};
var DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
var MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December"
];
function CalendarCard({
  title = "Calendar",
  date,
  events = [],
  onDateChange,
  className,
  dayNames = DAYS,
  monthNames = MONTHS
}) {
  const [currentMonth, setCurrentMonth] = React17.useState(date);
  const [selectedDate, setSelectedDate] = React17.useState(date);
  const firstDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
  const lastDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
  const startingDayOfWeek = firstDayOfMonth.getDay();
  const daysInMonth = lastDayOfMonth.getDate();
  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };
  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };
  const handleDateClick = (day) => {
    const newDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    setSelectedDate(newDate);
    onDateChange?.(newDate);
  };
  const isToday = (day) => {
    const today = /* @__PURE__ */ new Date();
    return day === today.getDate() && currentMonth.getMonth() === today.getMonth() && currentMonth.getFullYear() === today.getFullYear();
  };
  const isSelected = (day) => {
    return day === selectedDate.getDate() && currentMonth.getMonth() === selectedDate.getMonth() && currentMonth.getFullYear() === selectedDate.getFullYear();
  };
  const days = [];
  for (let i = 0; i < startingDayOfWeek; i++) {
    days.push(null);
  }
  for (let i = 1; i <= daysInMonth; i++) {
    days.push(i);
  }
  return /* @__PURE__ */ jsxs42(
    "div",
    {
      className: cn(
        "rounded-xl border border-[var(--border)] bg-[var(--surface)] shadow-card",
        className
      ),
      children: [
        /* @__PURE__ */ jsx68("div", { className: "border-b border-[var(--border)] px-5 py-4", children: /* @__PURE__ */ jsx68("h2", { className: "text-sm font-semibold text-[var(--text-primary)]", children: title }) }),
        /* @__PURE__ */ jsxs42("div", { className: "p-4", children: [
          /* @__PURE__ */ jsxs42("div", { className: "flex items-center justify-between mb-4", children: [
            /* @__PURE__ */ jsx68(
              "button",
              {
                type: "button",
                onClick: prevMonth,
                className: "p-1 rounded hover:bg-[var(--neutral-100)] text-[var(--text-secondary)]",
                "aria-label": "Previous month",
                children: /* @__PURE__ */ jsx68("svg", { className: "h-5 w-5", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", strokeWidth: 2, "aria-hidden": "true", children: /* @__PURE__ */ jsx68("path", { strokeLinecap: "round", strokeLinejoin: "round", d: "M15 19l-7-7 7-7" }) })
              }
            ),
            /* @__PURE__ */ jsxs42("span", { className: "text-sm font-medium text-[var(--text-primary)]", children: [
              monthNames[currentMonth.getMonth()],
              " ",
              currentMonth.getFullYear()
            ] }),
            /* @__PURE__ */ jsx68(
              "button",
              {
                type: "button",
                onClick: nextMonth,
                className: "p-1 rounded hover:bg-[var(--neutral-100)] text-[var(--text-secondary)]",
                "aria-label": "Next month",
                children: /* @__PURE__ */ jsx68("svg", { className: "h-5 w-5", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", strokeWidth: 2, "aria-hidden": "true", children: /* @__PURE__ */ jsx68("path", { strokeLinecap: "round", strokeLinejoin: "round", d: "M9 5l7 7-7 7" }) })
              }
            )
          ] }),
          /* @__PURE__ */ jsx68("div", { className: "grid grid-cols-7 gap-1 mb-2", children: dayNames.map((day) => /* @__PURE__ */ jsx68(
            "div",
            {
              className: "text-center text-xs font-medium text-[var(--text-secondary)] py-1",
              children: day
            },
            day
          )) }),
          /* @__PURE__ */ jsx68("div", { className: "grid grid-cols-7 gap-1", children: days.map(
            (day, index) => day === null ? /* @__PURE__ */ jsx68("div", { className: "h-8 w-8", "aria-hidden": "true" }, `empty-${index}`) : /* @__PURE__ */ jsx68(
              "button",
              {
                type: "button",
                onClick: () => handleDateClick(day),
                "aria-label": `${monthNames[currentMonth.getMonth()]} ${day}, ${currentMonth.getFullYear()}${isToday(day) ? " (today)" : ""}${isSelected(day) ? " (selected)" : ""}`,
                "aria-pressed": isSelected(day),
                className: cn(
                  "h-8 w-8 rounded-full text-xs transition-colors hover:bg-[var(--neutral-100)]",
                  isToday(day) && !isSelected(day) && "bg-[var(--primary-50)] text-[var(--primary-600)]",
                  isSelected(day) && "bg-[var(--primary-500)] text-white",
                  !isToday(day) && !isSelected(day) && "text-[var(--text-primary)]"
                ),
                children: day
              },
              `day-${day}`
            )
          ) }),
          events.length > 0 && /* @__PURE__ */ jsxs42("div", { className: "mt-4 space-y-2", children: [
            /* @__PURE__ */ jsx68("h3", { className: "text-xs font-medium text-[var(--text-secondary)] uppercase", children: "Events" }),
            events.map((event) => /* @__PURE__ */ jsxs42(
              "div",
              {
                className: cn(
                  "rounded-md border-l-2 px-3 py-2",
                  eventColorStyles[event.color || "primary"]
                ),
                children: [
                  /* @__PURE__ */ jsx68("p", { className: "text-sm font-medium text-[var(--text-primary)]", children: event.title }),
                  /* @__PURE__ */ jsx68("p", { className: "text-xs text-[var(--text-secondary)]", children: event.time })
                ]
              },
              event.id
            ))
          ] })
        ] })
      ]
    }
  );
}

// src/lib/animations.ts
var getTransition = (duration = 0.3, delay = 0) => {
  return {
    duration: Math.min(duration, 0.6),
    // Max 600ms per animation budget
    delay,
    ease: [0.4, 0, 0.2, 1]
    // Standard easing
  };
};
var getSpringTransition = (stiffness = 300, damping = 30) => {
  return {
    type: "spring",
    stiffness,
    damping
  };
};
var fadeIn = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: getTransition(0.3)
  }
};
var slideUp = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: getTransition(0.4)
  }
};
var scale = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: getTransition(0.3)
  }
};
var staggerContainer = (staggerDelay = 0.1) => ({
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: staggerDelay,
      delayChildren: 0.2
    }
  }
});
var prefersReducedMotion = () => {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
};
var withReducedMotion = (variants) => {
  return prefersReducedMotion() ? {} : variants;
};
var hoverScale = {
  scale: 1.05,
  transition: getSpringTransition(400, 25)
};
var tapScale = {
  scale: 0.95,
  transition: getSpringTransition(400, 25)
};
var buttonPress = {
  whileHover: hoverScale,
  whileTap: tapScale
};

// src/tokens.json
var tokens_default = {
  color: {
    primary: {
      "50": "#EEF2FF",
      "100": "#E0E7FF",
      "200": "#C7D2FE",
      "300": "#A5B4FC",
      "400": "#818CF8",
      "500": "#4D7CFE",
      "600": "#4338CA",
      "700": "#3730A3",
      "800": "#312E81",
      "900": "#1E1B4B",
      "text-aaa": "#3730A3"
    },
    accent: {
      purple: {
        "50": "#faf5ff",
        "100": "#f3e8ff",
        "200": "#e9d5ff",
        "300": "#d8b4fe",
        "400": "#c084fc",
        "500": "#8b5cf6",
        "600": "#7c3aed",
        "700": "#6d28d9",
        "800": "#5b21b6",
        "900": "#4c1d95"
      },
      pink: {
        "50": "#FFF0F6",
        "100": "#FE4D9726",
        "200": "#FFB3D1",
        "300": "#FF85B8",
        "400": "#FF5CA0",
        "500": "#FE4D97",
        "600": "#E63D82",
        "700": "#CC2D6D",
        "800": "#B31D58",
        "900": "#990D43"
      },
      cyan: {
        "50": "#E6FCFE",
        "100": "#2CE5F626",
        "200": "#99F2F9",
        "300": "#66EBF7",
        "400": "#33E4F5",
        "500": "#2CE5F6",
        "600": "#00C4D4",
        "700": "#00A3B2",
        "800": "#008290",
        "900": "#00616E"
      },
      orange: {
        "50": "#FFF8E6",
        "100": "#FFAB2B26",
        "200": "#FFD699",
        "300": "#FFC266",
        "400": "#FFAE33",
        "500": "#FFAB2B",
        "600": "#E69500",
        "700": "#CC8400",
        "800": "#B37300",
        "900": "#996200"
      }
    },
    semantic: {
      success: {
        "50": "#EEFBE8",
        "100": "#6DD23026",
        "200": "#B8E89E",
        "300": "#93DC6E",
        "400": "#6DD230",
        "500": "#6DD230",
        "600": "#5AB828",
        "700": "#479E20",
        "800": "#348418",
        "900": "#216A10",
        "text-aaa": "#2D7A15"
      },
      error: {
        "50": "#fef2f2",
        "100": "#fee2e2",
        "200": "#fecaca",
        "300": "#fca5a5",
        "400": "#f87171",
        "500": "#ef4444",
        "600": "#dc2626",
        "700": "#b91c1c",
        "800": "#991b1b",
        "900": "#7f1d1d",
        "text-aaa": "#A91C1C"
      },
      warning: {
        "50": "#FFF8E6",
        "100": "#FFAB2B26",
        "200": "#FFD699",
        "300": "#FFC266",
        "400": "#FFAE33",
        "500": "#FFAB2B",
        "600": "#E69500",
        "700": "#CC8400",
        "800": "#B37300",
        "900": "#996200",
        "text-aaa": "#996200"
      },
      info: {
        "50": "#eff6ff",
        "100": "#dbeafe",
        "200": "#bfdbfe",
        "300": "#93c5fd",
        "400": "#60a5fa",
        "500": "#0ea5e9",
        "600": "#0284c7",
        "700": "#0369a1",
        "800": "#075985",
        "900": "#0c4a6e",
        "text-aaa": "#005A9C"
      }
    },
    neutral: {
      "50": "#F8FAFB",
      "100": "#F2F4F6",
      "200": "#E8ECEF",
      "300": "#D1D5DB",
      "400": "#98A9BC",
      "500": "#778CA2",
      "600": "#525252",
      "700": "#404040",
      "800": "#252631",
      "900": "#1A1A2E"
    },
    background: {
      base: "#F5F6F7",
      surface: "#ffffff",
      overlay: "rgba(0, 0, 0, 0.4)"
    }
  },
  font: {
    family: {
      primary: "'Public Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      secondary: "'Public Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      mono: "'IBM Plex Mono', 'Courier New', monospace",
      icons: "'Material Symbols Outlined', sans-serif"
    },
    size: {
      caption: "12px",
      small: "14px",
      body: "16px",
      heading3: "20px",
      heading2: "28px",
      heading1: "36px",
      display: "48px"
    },
    lineHeight: {
      caption: "16px",
      small: "20px",
      body: "24px",
      heading3: "28px",
      heading2: "36px",
      heading1: "44px",
      display: "60px"
    },
    weight: {
      regular: "400",
      medium: "500",
      semibold: "600",
      bold: "700"
    }
  },
  space: {
    xs: "4px",
    sm: "8px",
    md: "16px",
    lg: "24px",
    xl: "32px",
    "2xl": "48px",
    "3xl": "64px",
    "4xl": "96px"
  },
  radius: {
    sm: "4px",
    md: "8px",
    lg: "12px",
    xl: "16px",
    "2xl": "24px",
    full: "9999px"
  },
  shadow: {
    sm: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
    md: "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)",
    lg: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)",
    xl: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)",
    "2xl": "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)"
  },
  animation: {
    duration: {
      instant: "50ms",
      fast: "150ms",
      normal: "300ms",
      slow: "500ms"
    },
    easing: {
      linear: "linear",
      easeIn: "cubic-bezier(0.4, 0, 1, 1)",
      easeOut: "cubic-bezier(0, 0, 0.2, 1)",
      easeInOut: "cubic-bezier(0.4, 0, 0.2, 1)",
      spring: "cubic-bezier(0.22, 1, 0.36, 1)"
    }
  },
  breakpoint: {
    mobile: "375px",
    tablet: "768px",
    desktop: "1280px"
  },
  card: {
    icon: {
      kpi: {
        size: "40px",
        containerSize: "40px",
        shape: "9999px",
        description: "KPI/metric cards - large circular icon for data visualization emphasis"
      },
      status: {
        size: "16px",
        containerSize: "28px",
        shape: "8px",
        description: "Status/filter cards - compact square icon for interactive filtering"
      }
    }
  },
  accessibility: {
    "wcag-aaa": {
      contrast: {
        "normal-text": "7:1",
        "large-text": "4.5:1",
        "ui-components": "3:1"
      },
      colors: {
        "primary-text": "#005A9C",
        "success-text": "#0D5C3D",
        "error-text": "#A91C1C",
        "warning-text": "#92400E",
        "info-text": "#005A9C"
      }
    },
    focus: {
      "outline-width": "3px",
      "outline-offset": "2px",
      "outline-color": "#0284c7",
      primary: "#0051D0",
      light: "#0284c7"
    },
    "touch-target": {
      "min-size": "44px"
    },
    animation: {
      "reduced-motion-duration": "0.01ms"
    }
  }
};

// src/design-tokens.ts
var getToken = (path) => {
  const value = path.split(".").reduce((obj, key) => obj?.[key], tokens_default);
  return value !== void 0 ? String(value) : void 0;
};
var colors = {
  primary: tokens_default.color.primary,
  accent: tokens_default.color.accent,
  semantic: tokens_default.color.semantic,
  neutral: tokens_default.color.neutral,
  background: tokens_default.color.background
};
var typography = {
  family: tokens_default.font.family,
  size: tokens_default.font.size,
  weight: tokens_default.font.weight,
  lineHeight: tokens_default.font.lineHeight
};
var spacing = tokens_default.space;
var radius = tokens_default.radius;
var shadows = tokens_default.shadow;
var animations = tokens_default.animation;
var breakpoints = tokens_default.breakpoint;
var accessibility = tokens_default.accessibility;
var card = tokens_default.card;
var flattenTokens = (obj, prefix) => {
  const result = {};
  for (const [key, value] of Object.entries(obj)) {
    const cssVarName = `--${prefix}-${key}`;
    if (typeof value === "object" && value !== null && !Array.isArray(value)) {
      const nested = flattenTokens(value, `${prefix}-${key}`);
      Object.assign(result, nested);
    } else {
      result[cssVarName] = String(value);
    }
  }
  return result;
};
var getCSSVariables = () => {
  const cssVars = {};
  Object.assign(cssVars, flattenTokens(colors.primary, "color-primary"));
  for (const [accentName, accentShades] of Object.entries(colors.accent)) {
    Object.assign(cssVars, flattenTokens(accentShades, `color-accent-${accentName}`));
  }
  for (const [semanticName, semanticShades] of Object.entries(colors.semantic)) {
    Object.assign(cssVars, flattenTokens(semanticShades, `color-${semanticName}`));
  }
  Object.assign(cssVars, flattenTokens(colors.neutral, "color-neutral"));
  Object.assign(cssVars, flattenTokens(colors.background, "color-background"));
  Object.assign(cssVars, flattenTokens(spacing, "spacing"));
  Object.assign(cssVars, flattenTokens(radius, "radius"));
  Object.assign(cssVars, flattenTokens(shadows, "shadow"));
  Object.assign(cssVars, flattenTokens(typography.family, "font-family"));
  Object.assign(cssVars, flattenTokens(typography.size, "font-size"));
  Object.assign(cssVars, flattenTokens(typography.weight, "font-weight"));
  Object.assign(cssVars, flattenTokens(typography.lineHeight, "line-height"));
  Object.assign(cssVars, flattenTokens(animations.duration, "animation-duration"));
  Object.assign(cssVars, flattenTokens(animations.easing, "animation-easing"));
  Object.assign(cssVars, flattenTokens(breakpoints, "breakpoint"));
  if (accessibility["wcag-aaa"]?.colors) {
    Object.assign(cssVars, flattenTokens(accessibility["wcag-aaa"].colors, "a11y-color"));
  }
  if (accessibility.focus) {
    Object.assign(cssVars, flattenTokens(accessibility.focus, "a11y-focus"));
  }
  if (card?.icon) {
    Object.assign(cssVars, flattenTokens(card.icon, "card-icon"));
  }
  return cssVars;
};
var applyDesignTokens = (scope) => {
  let target;
  if (!scope) {
    target = document.documentElement;
  } else if (typeof scope === "string") {
    const element = document.querySelector(scope);
    target = element || document.documentElement;
  } else {
    target = scope;
  }
  const cssVars = getCSSVariables();
  Object.entries(cssVars).forEach(([property, value]) => {
    target.style.setProperty(property, value);
  });
  return target;
};

// src/index.ts
import { Slot as Slot7 } from "@radix-ui/react-slot";
export {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  ActivityListItem,
  ActivityListPanel,
  AdminShell,
  AdminSidebar,
  AdminTopbar,
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogTitle,
  AlertDialogTrigger,
  AlertTitle,
  AppleButton,
  AspectRatio,
  Avatar,
  AvatarFallback,
  AvatarImage,
  AvatarStack,
  Badge,
  Breadcrumb,
  BreadcrumbEllipsis,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Button,
  Calendar,
  CalendarCard,
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartStyle,
  ChartTooltip,
  ChartTooltipContent,
  Checkbox,
  CircleProgressCard,
  Collapsible,
  CollapsibleContent2 as CollapsibleContent,
  CollapsibleTrigger2 as CollapsibleTrigger,
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
  ContextMenu,
  ContextMenuCheckboxItem,
  ContextMenuContent,
  ContextMenuGroup,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuPortal,
  ContextMenuRadioGroup,
  ContextMenuRadioItem,
  ContextMenuSeparator,
  ContextMenuShortcut,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerPortal,
  DrawerTitle,
  DrawerTrigger,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
  Input,
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
  Label3 as Label,
  Menubar,
  MenubarCheckboxItem,
  MenubarContent,
  MenubarGroup,
  MenubarItem,
  MenubarLabel,
  MenubarMenu,
  MenubarPortal,
  MenubarRadioGroup,
  MenubarRadioItem,
  MenubarSeparator,
  MenubarShortcut,
  MenubarSub,
  MenubarSubContent,
  MenubarSubTrigger,
  MenubarTrigger,
  MetricCard,
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuIndicator,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  NavigationMenuViewport,
  PageScaffold,
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
  Progress,
  ProgressTrack,
  RadioGroup4 as RadioGroup,
  RadioGroupItem,
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
  ScrollArea,
  ScrollBar,
  SectionCard,
  SectionTemplate,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
  Separator5 as Separator,
  SettingsCard,
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInput,
  SidebarInset,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarRail,
  SidebarSeparator,
  SidebarTrigger,
  Skeleton,
  Slider,
  Slot7 as Slot,
  StatCard,
  StatusBadge,
  StatusCard,
  Switch,
  SystemStatusList,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  TaskListSection,
  TaskRow,
  Textarea,
  TimelineList,
  Toaster,
  Toggle,
  ToggleGroup,
  ToggleGroupItem,
  Tooltip2 as Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  accessibility,
  animations,
  appleButtonVariants,
  applyDesignTokens,
  badgeVariants,
  breakpoints,
  buttonPress,
  buttonVariants,
  card,
  cn,
  colors,
  fadeIn,
  getCSSVariables,
  getSpringTransition,
  getToken,
  getTransition,
  hoverScale,
  navigationMenuTriggerStyle,
  prefersReducedMotion,
  progressVariants,
  radius,
  scale,
  shadows,
  slideUp,
  spacing,
  staggerContainer,
  statusBadgeVariants,
  statusCardVariants,
  tapScale,
  toggleVariants,
  typography,
  useFormField,
  withReducedMotion
};
